import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminService from '../services/adminService';
import AddNewMenuItemModal from '../components/AddNewMenuItemModal';
import EditMenuItemModal from '../components/EditMenuItemModal';
import AddProductModal from '../components/AddProductModal';
import EditProductModal from '../components/EditProductModal';
import { message, Table, Space, Tag, Button, Modal, Form, Input, Select, Upload, Radio } from 'antd';
import { API_ENDPOINTS } from '../utils/config';
import BusinessStaffManagement from '../components/BusinessStaffManagement';
import '../css/admin/BusinessDetailsTabbed.css?v=90';
import {
  MdOutlineDashboard,
  MdOutlineRestaurantMenu,
  MdOutlineShoppingCart,
  MdOutlineAttachMoney,
  MdOutlineStarOutline,
  MdOutlineDescription,
  MdOutlineLocalOffer,
  MdOutlineAccessTime,
  MdOutlineLocalShipping,
  MdOutlineSettings,
  MdOutlineDeliveryDining,
  MdOutlineCheckCircleOutline,
  MdOutlineLocationOn,
  MdOutlineSpeed,
  MdOutlinePerson,
  MdOutlinePhone,
  MdOutlineEdit,
  MdOutlineBarChart,
  MdOutlineAdd,
  MdOutlineMessage,
  MdOutlineCurrencyRupee,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineInfo,
} from 'react-icons/md';
import { FiMapPin, FiPlus, FiEdit2, FiTrash2, FiList, FiEye, FiEyeOff, FiShoppingCart, FiDollarSign, FiStar, FiBarChart, FiCheckCircle, FiClock, FiPackage, FiTruck, FiCalendar, FiTrendingUp, FiUsers, FiInfo, FiUser, FiPhone, FiHome, FiX, FiDownload, FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi';
import { MdOutlineTwoWheeler, MdOutlineStorefront, MdOutlineReceiptLong } from 'react-icons/md';
import * as XLSX from 'xlsx';

// Force cache refresh - Updated CSS with better scrolling
const CACHE_VERSION = '2.0';

const NoDataDisplay = ({ message, colSpan, className = '' }) => {
  if (colSpan) {
    return (
      <tr className="no-data-row">
        <td colSpan={colSpan} className={`no-data no-data-block ${className}`}>
          <MdOutlineInfo className="no-data-icon" />
          <span>{message}</span>
        </td>
      </tr>
    );
  }

  return (
    <div className={`no-data no-data-block ${className}`}>
      <MdOutlineInfo className="no-data-icon" />
      <span>{message}</span>
    </div>
  );
};

// Simple Offer Form Component
const SimpleOfferForm = ({ offer, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState({
    title: offer?.title || '',
    offer_type: offer?.offer_type || 'general',
    description: offer?.description || '',
    discount_percentage: offer?.discount_percentage || '',
    discount_amount: offer?.discount_amount || '',
    original_price: offer?.original_price || '',
    offer_price: offer?.offer_price || '',
    is_active: offer?.is_active !== undefined ? offer.is_active : true,
    is_approved: offer?.is_approved !== undefined ? offer.is_approved : false,
    valid_from: offer?.valid_from ? offer.valid_from.split('T')[0] : '',
    valid_to: offer?.valid_to ? offer.valid_to.split('T')[0] : '',
    priority: offer?.priority || 0,
    max_views: offer?.max_views || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Offer Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., Buy 1 Get 1 Free"
          required
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label>Offer Type *</label>
          <select
            name="offer_type"
            value={formData.offer_type}
            onChange={handleChange}
            required
          >
            <option value="general">General</option>
            <option value="product">Product Specific</option>
            <option value="category">Category Specific</option>
            <option value="seasonal">Seasonal</option>
            <option value="flash">Flash Sale</option>
          </select>
        </div>

        <div className="form-group">
          <label>Priority</label>
          <input
            type="number"
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            placeholder="0"
            min="0"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Offer description..."
          rows="3"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label>Discount Percentage (%)</label>
          <input
            type="number"
            name="discount_percentage"
            value={formData.discount_percentage}
            onChange={handleChange}
            placeholder="e.g., 20"
            min="0"
            max="100"
            step="0.01"
          />
        </div>

        <div className="form-group">
          <label>Discount Amount (?)</label>
          <input
            type="number"
            name="discount_amount"
            value={formData.discount_amount}
            onChange={handleChange}
            placeholder="e.g., 100"
            min="0"
            step="0.01"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label>Valid From</label>
          <input
            type="date"
            name="valid_from"
            value={formData.valid_from}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Valid Until</label>
          <input
            type="date"
            name="valid_to"
            value={formData.valid_to}
            onChange={handleChange}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '20px', marginTop: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            style={{ width: 'auto' }}
          />
          Active
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            name="is_approved"
            checked={formData.is_approved}
            onChange={handleChange}
            style={{ width: 'auto' }}
          />
          Approved
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : (offer ? 'Update Offer' : 'Create Offer')}
        </button>
      </div>
    </form>
  );
};

// Simple Coupon Form Component
const SimpleCouponForm = ({ coupon, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState({
    coupon_code: coupon?.code || '',
    discount_type: coupon?.type || 'percentage',
    discount_value: coupon?.value || '',
    is_active: coupon?.is_active !== undefined ? coupon.is_active : true,
    valid_from: coupon?.valid_from ? coupon.valid_from.split('T')[0] : '',
    valid_to: coupon?.valid_to ? coupon.valid_to.split('T')[0] : '',
    max_usage_total: coupon?.max_usage || '',
    min_order_value: coupon?.min_order_value || '',
    max_discount_amount: coupon?.max_discount_amount || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Coupon Code *</label>
        <input
          type="text"
          name="coupon_code"
          value={formData.coupon_code}
          onChange={handleChange}
          placeholder="e.g., SAVE20"
          required
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label>Discount Type *</label>
          <select
            name="discount_type"
            value={formData.discount_type}
            onChange={handleChange}
            required
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        <div className="form-group">
          <label>Discount Value *</label>
          <input
            type="number"
            name="discount_value"
            value={formData.discount_value}
            onChange={handleChange}
            placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
            required
            min="0"
            step="0.01"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label>Valid From</label>
          <input
            type="date"
            name="valid_from"
            value={formData.valid_from}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Valid Until</label>
          <input
            type="date"
            name="valid_to"
            value={formData.valid_to}
            onChange={handleChange}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label>Max Usage</label>
          <input
            type="number"
            name="max_usage_total"
            value={formData.max_usage_total}
            onChange={handleChange}
            placeholder="Unlimited"
            min="0"
          />
        </div>

        <div className="form-group">
          <label>Min Order Value</label>
          <input
            type="number"
            name="min_order_value"
            value={formData.min_order_value}
            onChange={handleChange}
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div className="form-group">
          <label>Max Discount</label>
          <input
            type="number"
            name="max_discount_amount"
            value={formData.max_discount_amount}
            onChange={handleChange}
            placeholder="No limit"
            min="0"
            step="0.01"
          />
        </div>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            style={{ width: 'auto' }}
          />
          Active
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : (coupon ? 'Update Coupon' : 'Create Coupon')}
        </button>
      </div>
    </form>
  );
};

const CategoriesTab = ({ businessId, items = [] }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [form] = Form.useForm();

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.ADMIN_BASE}/business-management/${businessId}/categories/`);
      const result = await response.json();
      if (result.success) {
        // Flatten data to show subcategories as normal rows
        const flatData = [];
        (result.data || []).forEach(cat => {
          flatData.push({ ...cat });
          if (cat.sub_categories && cat.sub_categories.length > 0) {
            cat.sub_categories.forEach(sub => {
              flatData.push({
                ...sub,
                parent_category_id: cat.category_id
              });
            });
          }
        });
        setCategories(flatData);
      } else {
        message.error(result.message || 'Failed to load categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchCategories();
    }
  }, [businessId]);

  // Helper to check if any business items/products are assigned to a category
  const getAssignedItems = async (record) => {
    let currentItems = items;
    if (!currentItems || currentItems.length === 0) {
      try {
        const response = await AdminService.getBusinessProducts(businessId);
        if (response.success || Array.isArray(response.data)) {
          const raw = response.data || [];
          currentItems = raw.map(p => ({
            item_id: p.item_id,
            name: p.item_name,
            category_id: p.category_id || p.parent_category_id,
            sub_category_id: p.sub_category_id,
            category: p.category_name || p.item_category || '',
            sub_category: p.sub_category_name || p.sub_category || '',
            _raw: p
          }));
        }
      } catch (err) {
        console.error('Error fetching items for category check:', err);
      }
    }

    if (!currentItems || currentItems.length === 0) return [];

    const catIdStr = record?.category_id != null ? String(record.category_id).toLowerCase().trim() : '';
    const catNameStr = (record?.category_name || record?.name) ? String(record.category_name || record.name).toLowerCase().trim() : '';

    return currentItems.filter(item => {
      const raw = item._raw || {};

      const itemCatId = String(item.category_id || raw.category_id || raw.parent_category_id || '').toLowerCase().trim();
      const itemSubCatId = String(item.sub_category_id || raw.sub_category_id || '').toLowerCase().trim();

      const itemCatName = String(item.category || item.category_name || item.item_category || raw.category_name || raw.item_category || '').toLowerCase().trim();
      const itemSubCatName = String(item.sub_category || item.sub_category_name || raw.sub_category_name || raw.sub_category || '').toLowerCase().trim();

      const matchesId = catIdStr && (itemCatId === catIdStr || itemSubCatId === catIdStr);
      const matchesName = catNameStr && (itemCatName === catNameStr || itemSubCatName === catNameStr);

      return Boolean(matchesId || matchesName);
    });
  };

  const handleCreateCategory = async (values) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append('category_name', values.category_name);
      if (values.category_type === 'sub' && values.parent_category_id) {
        formData.append('parent_category_id', values.parent_category_id);
      }
      if (values.category_image && values.category_image.length > 0) {
        formData.append('category_image', values.category_image[0].originFileObj);
      }

      const url = editingCategory
        ? `${API_ENDPOINTS.ADMIN_BASE}/business-management/${businessId}/categories/${editingCategory.category_id}/`
        : `${API_ENDPOINTS.ADMIN_BASE}/business-management/${businessId}/categories/`;

      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        body: formData,
      });
      const result = await response.json();

      if (result.success) {
        message.success(`Category ${editingCategory ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        setEditingCategory(null);
        form.resetFields();
        fetchCategories();
      } else {
        message.error(result.message || `Failed to ${editingCategory ? 'update' : 'create'} category`);
      }
    } catch (error) {
      console.error(`Error ${editingCategory ? 'updating' : 'creating'} category:`, error);
      message.error(`An error occurred while ${editingCategory ? 'updating' : 'creating'} the category`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditClick = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({
      category_name: record.category_name || record.name,
      category_type: record.parent_category_id ? 'sub' : 'top',
      parent_category_id: record.parent_category_id
    });
    setIsModalVisible(true);
  };

  const handleDeleteCategory = async (record) => {
    const categoryId = typeof record === 'object' ? record.category_id : record;
    const categoryObj = typeof record === 'object' ? record : categories.find(c => String(c.category_id) === String(categoryId)) || { category_id: categoryId };

    try {
      setActionLoadingId(categoryId);
      message.loading({ content: 'Checking assigned items...', key: 'checkingCategoryItems', duration: 0 });
      const assignedItems = await getAssignedItems(categoryObj);
      message.destroy('checkingCategoryItems');

      if (assignedItems && assignedItems.length > 0) {
        const count = assignedItems.length;
        const categoryName = categoryObj.category_name || categoryObj.name || 'This category';
        Modal.warning({
          title: 'Cannot Delete Category',
          content: (
            <div>
              <p>This category (<strong>"{categoryName}"</strong>) has been assigned to <strong>{count}</strong> item{count > 1 ? 's' : ''}.</p>
              <p style={{ color: '#d9534f', marginBottom: 0 }}>Please update or reassign those items before deleting this category.</p>
            </div>
          ),
          okText: 'Understood'
        });
        return;
      }

      Modal.confirm({
        title: 'Are you sure you want to delete this category?',
        content: 'This action cannot be undone.',
        okText: 'Yes, Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            setActionLoadingId(categoryId);
            const response = await fetch(`${API_ENDPOINTS.ADMIN_BASE}/business-management/${businessId}/categories/${categoryId}/`, {
              method: 'DELETE',
            });
            const result = await response.json();
            if (result.success) {
              message.success('Category deleted successfully');
              fetchCategories();
            } else {
              message.error(result.message || 'Failed to delete category');
            }
          } catch (error) {
            console.error('Error deleting category:', error);
            message.error('An error occurred while deleting the category');
          } finally {
            setActionLoadingId(null);
          }
        }
      });
    } catch (err) {
      console.error('Error checking category items:', err);
      message.destroy('checkingCategoryItems');
      message.error('Failed to verify assigned items');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleToggleStatus = async (record) => {
    const categoryId = typeof record === 'object' ? record.category_id : record;
    const categoryObj = typeof record === 'object' ? record : categories.find(c => String(c.category_id) === String(categoryId)) || { category_id: categoryId };
    const isActive = categoryObj.is_active || categoryObj.status === 'active' || categoryObj.status === 1 || categoryObj.is_active === 1;

    try {
      setActionLoadingId(categoryId);
      if (isActive) {
        message.loading({ content: 'Checking assigned items...', key: 'checkingCategoryItems', duration: 0 });
        const assignedItems = await getAssignedItems(categoryObj);
        message.destroy('checkingCategoryItems');
        if (assignedItems && assignedItems.length > 0) {
          const count = assignedItems.length;
          const categoryName = categoryObj.category_name || categoryObj.name || 'This category';
          Modal.warning({
            title: 'Cannot Deactivate Category',
            content: (
              <div>
                <p>This category (<strong>"{categoryName}"</strong>) has been assigned to <strong>{count}</strong> item{count > 1 ? 's' : ''}.</p>
                <p style={{ color: '#d9534f', marginBottom: 0 }}>Please update or reassign those items before deactivating this category.</p>
              </div>
            ),
            okText: 'Understood'
          });
          return;
        }
      }

      const response = await fetch(`${API_ENDPOINTS.ADMIN_BASE}/business-management/${businessId}/categories/${categoryId}/`, {
        method: 'PATCH',
      });
      const result = await response.json();
      if (result.success) {
        message.success('Category status updated successfully');
        fetchCategories();
      } else {
        message.error(result.message || 'Failed to update category status');
      }
    } catch (error) {
      message.destroy('checkingCategoryItems');
      console.error('Error updating category status:', error);
      message.error('An error occurred while updating category status');
    } finally {
      setActionLoadingId(null);
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const columns = [
    {
      title: 'Category ID',
      dataIndex: 'category_id',
      key: 'category_id',
    },
    {
      title: 'Parent ID',
      key: 'parent_category_id',
      render: (_, record) => record.parent_category_id || '-'
    },
    {
      title: 'Image',
      key: 'image',
      render: (_, record) => {
        const url = record.category_image || record.image;
        return url ? <img src={url} alt="category" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-';
      }
    },
    {
      title: 'Category Name',
      key: 'category_name',
      render: (_, record) => <strong>{record.category_name || record.name}</strong>
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        record.is_active || record.status === 'active' || record.status === 1 || record.is_active === 1
          ? <Tag color="green">Active</Tag>
          : <Tag color="red">Inactive</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isActive = record.is_active || record.status === 'active' || record.status === 1 || record.is_active === 1;
        const isActionLoading = actionLoadingId === record.category_id;
        return (
          <Space size="small">
            <Button
              type="text"
              icon={isActive ? <FiEyeOff /> : <FiEye />}
              size="small"
              loading={isActionLoading}
              disabled={isActionLoading}
              title={isActive ? 'Deactivate' : 'Activate'}
              onClick={() => handleToggleStatus(record)}
            >
              {isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button type="text" icon={<FiEdit2 />} size="small" disabled={isActionLoading} onClick={() => handleEditClick(record)}>
              Edit
            </Button>
            <Button type="text" danger icon={<FiTrash2 />} size="small" loading={isActionLoading} disabled={isActionLoading} onClick={() => handleDeleteCategory(record)}>
              Delete
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <div className="tab-section fade-in">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="section-title">Categories</h2>
        <Button type="primary" icon={<FiPlus />} onClick={() => {
          setEditingCategory(null);
          form.resetFields();
          setIsModalVisible(true);
        }}>
          Add New Category
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={categories}
        rowKey={(record) => record.id || record.category_id || record.name || Math.random()}
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
      <Modal
        title={editingCategory ? "Edit Category" : "Add New Category"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreateCategory} initialValues={{ category_type: 'top' }}>
          {!editingCategory && (
            <Form.Item name="category_type" label="Category Type">
              <Radio.Group>
                <Radio value="top">Top Level Category</Radio>
                <Radio value="sub">Subcategory</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item
            name="category_name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.category_type !== currentValues.category_type}
          >
            {({ getFieldValue }) =>
              getFieldValue('category_type') === 'sub' ? (
                <Form.Item
                  name="parent_category_id"
                  label="Parent Category"
                  rules={[{ required: true, message: 'Please select a parent category' }]}
                >
                  <Select placeholder="Select parent category">
                    {categories.filter(c => !c.parent_category_id).map(cat => (
                      <Select.Option key={cat.category_id} value={cat.category_id}>
                        {cat.category_name} (ID: {cat.category_id})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="category_image"
            label="Category Image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload beforeUpload={() => false} maxCount={1} listType="picture">
              <Button icon={<FiPlus />}>Click to Upload</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setIsModalVisible(false); setEditingCategory(null); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingCategory ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const BusinessDetailsTabbed = ({ businessId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [menuItemsLoading, setMenuItemsLoading] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Initialize with default date range (1st of current month to today)
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // Format dates as YYYY-MM-DD for date input compatibility
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [currentPeriod, setCurrentPeriod] = useState({
    startDate: formatDate(firstOfMonth),
    endDate: formatDate(today)
  });
  const [error, setError] = useState(null);


  // Helper function to clean item names by removing JSON-like text
  const cleanItemName = (name) => {
    if (!name) return '';
    // Remove any JSON-like patterns: {"key": "value"} or {"key": value}
    return name.replace(/\s*\{[^}]*\}\s*/g, '').trim();
  };

  // Offers and Coupons modal state
  const [showOfferModal, setShowOfferModalState] = useState(false);
  const [showCouponModal, setShowCouponModalState] = useState(false);
  const [editingOffer, setEditingOfferState] = useState(null);
  const [editingCoupon, setEditingCouponState] = useState(null);
  const [isSavingOffer, setIsSavingOffer] = useState(false);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState(null); // item to delete
  const [deleteItemLoading, setDeleteItemLoading] = useState(false);

  // Memoize setters to prevent unnecessary re-renders
  const setShowOfferModal = useCallback((value) => {
    setShowOfferModalState(value);
  }, []);

  const setShowCouponModal = useCallback((value) => {
    setShowCouponModalState(value);
  }, []);

  const setEditingOffer = useCallback((value) => {
    setEditingOfferState(value);
  }, []);

  const setEditingCoupon = useCallback((value) => {
    setEditingCouponState(value);
  }, []);

  const fetchBusinessData = useCallback(async (period = '7days') => {
    try {
      setLoading(true);
      const response = await AdminService.getBusinessDetailedView(businessId, period);


      if (response.success) {
        setBusinessData(response.data);
        // Set performance data from the same response
        if (response.data.performance) {
          setPerformanceData(response.data.performance);
        }
      } else {
        console.error('API returned error:', response.message);
        setError(response.message || 'Failed to fetch business data');
      }
    } catch (err) {
      console.error('Error fetching business data:', err);
      setError(err.message || 'Failed to fetch business data');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (businessId) {
      fetchBusinessData(currentPeriod);
    }
  }, [businessId, currentPeriod, fetchBusinessData]);

  // Offer handlers
  const handleSaveOffer = useCallback(async (offerData) => {
    if (isSavingOffer) return;


    setIsSavingOffer(true);
    try {
      // Prepare data with proper field names
      const preparedData = {
        title: offerData.title,
        offer_type: offerData.offer_type,
        description: offerData.description,
        discount_percentage: offerData.discount_percentage || null,
        discount_amount: offerData.discount_amount || null,
        original_price: offerData.original_price || null,
        offer_price: offerData.offer_price || null,
        is_active: offerData.is_active,
        is_approved: offerData.is_approved,
        valid_from: offerData.valid_from || null,  // Already formatted as YYYY-MM-DD
        valid_to: offerData.valid_to || null,      // Already formatted as YYYY-MM-DD
        priority: parseInt(offerData.priority) || 0,
        max_views: offerData.max_views ? parseInt(offerData.max_views) : null
      };

      const response = editingOffer
        ? await AdminService.updateOffer(businessId, editingOffer.offer_id, preparedData)
        : await AdminService.createOffer(businessId, preparedData);

      if (response.success) {
        alert(editingOffer ? 'Offer updated successfully!' : 'Offer created successfully!');
        setShowOfferModal(false);
        setEditingOffer(null);
        setIsSavingOffer(false);
        await fetchBusinessData();
      } else {
        alert('Error: ' + response.message);
        setIsSavingOffer(false);
      }
    } catch (error) {
      console.error('Error saving offer:', error);
      alert('Error saving offer: ' + (error.message || 'Unknown error'));
      setIsSavingOffer(false);
    }
  }, [businessId, editingOffer, isSavingOffer, fetchBusinessData]);

  const handleCloseOfferModal = useCallback(() => {
    if (isSavingOffer) return;
    setShowOfferModal(false);
    setEditingOffer(null);
  }, [isSavingOffer]);

  // Coupon handlers
  const handleSaveCoupon = useCallback(async (couponData) => {
    if (isSavingOffer) return; // Reuse same saving flag


    setIsSavingOffer(true);
    try {
      // Prepare data with proper field names (only fields that exist in DB)
      const preparedData = {
        coupon_code: couponData.coupon_code,
        discount_type: couponData.discount_type,
        discount_value: parseFloat(couponData.discount_value),
        is_active: couponData.is_active,
        valid_from: couponData.valid_from || null,  // Already formatted as YYYY-MM-DD
        valid_to: couponData.valid_to || null,      // Already formatted as YYYY-MM-DD
        max_usage_total: couponData.max_usage_total ? parseInt(couponData.max_usage_total) : null
      };

      // Add coupon_id if editing
      if (editingCoupon) {
        preparedData.coupon_id = editingCoupon.coupon_id;
      }


      const response = editingCoupon
        ? await AdminService.updateCoupon(businessId, editingCoupon.coupon_id, preparedData)
        : await AdminService.createCoupon(businessId, preparedData);


      if (response.success) {
        alert(editingCoupon ? 'Coupon updated successfully!' : 'Coupon created successfully!');
        setShowCouponModal(false);
        setEditingCoupon(null);
        setIsSavingOffer(false);
        await fetchBusinessData();
      } else {
        alert('Error: ' + response.message);
        setIsSavingOffer(false);
      }
    } catch (error) {
      console.error('Error saving coupon:', error);
      alert('Error saving coupon: ' + (error.message || 'Unknown error'));
      setIsSavingOffer(false);
    }
  }, [businessId, editingCoupon, isSavingOffer, fetchBusinessData]);

  const handleCloseCouponModal = useCallback(() => {
    if (isSavingOffer) return;
    setShowCouponModal(false);
    setEditingCoupon(null);
  }, [isSavingOffer]);

  const fetchMenuItems = async () => {
    setMenuItemsLoading(true);
    try {
      const response = await AdminService.getBusinessProducts(businessId);
      if (response.success || Array.isArray(response.data)) {
        const raw = response.data || [];
        // Map new API shape to the shape MenuTab expects
        const mapped = raw.map(p => ({
          item_id: p.item_id,
          name: p.item_name,
          category: p.category_name || p.item_category || '',
          sub_category: p.sub_category_name || p.sub_category || '',
          type: p.item_type || 'Veg',
          item_type: p.item_type || '',
          image: p.media?.main_image || '',
          images: p.media?.images || [],
          description: p.description || '',
          is_active: p.is_active,
          status: p.is_active ? 'active' : 'inactive',
          rating: p.rating,
          rating_count: p.rating_count,
          is_bestseller: p.is_bestseller,
          is_new: p.is_new,
          is_customizable: p.is_customizable,
          badges: p.badges || [],
          availability_status: p.availability_status,
          stock_status: p.stock_status,
          stock_message: p.stock_message,
          preparation_time: p.preparation_time,
          charges: p.charges,
          discount_percentage: p.discount_percentage,
          // Aggregate price/stock from first variant for quick display
          price: p.selling_price || (p.variants?.[0]?.selling_price ?? ''),
          original_cost: p.original_cost || (p.variants?.[0]?.original_cost ?? ''),
          quantity: p.quantity ?? p.stock_quantity,
          variants: p.variants || [],
          // Keep original for view modal
          _raw: p,
        }));
        setMenuItems(mapped);
      } else {
        console.error('Menu items API failed:', response.message);
        setMenuItems([]);
      }
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setMenuItems([]);
    } finally {
      setMenuItemsLoading(false);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const response = await AdminService.getBusinessPerformanceMetrics(businessId, 30);
      if (response.success) {
        setPerformanceData(response.data);
      } else {
        // Fallback data if API fails
        setPerformanceData({
          total_orders: Math.floor(Math.random() * 1000) + 50,
          total_revenue: Math.floor(Math.random() * 500000) + 10000,
          avg_rating: (Math.random() * 2 + 3).toFixed(1),
          avg_order_value: Math.floor(Math.random() * 500) + 100
        });
      }
    } catch (err) {
      console.error('Error fetching performance data:', err);
      // Fallback data on error
      setPerformanceData({
        total_orders: Math.floor(Math.random() * 1000) + 50,
        total_revenue: Math.floor(Math.random() * 500000) + 10000,
        avg_rating: (Math.random() * 2 + 3).toFixed(1),
        avg_order_value: Math.floor(Math.random() * 500) + 100
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'menu' && menuItems.length === 0) {
      fetchMenuItems();
    }
  }, [activeTab]);

  const handleStatusToggle = async () => {
    // Implement status toggle
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <MdOutlineDashboard /> },
    { id: 'menu', label: 'Menu Management', icon: <MdOutlineRestaurantMenu /> },
    { id: 'categories', label: 'Categories', icon: <FiList /> },
    { id: 'orders', label: 'Order History', icon: <MdOutlineShoppingCart /> },
    { id: 'reviews', label: 'Reviews & Ratings', icon: <MdOutlineStarOutline /> },
    { id: 'offers', label: 'Offers & Coupons', icon: <MdOutlineLocalOffer /> },
    { id: 'staff', label: 'Staff Management', icon: <FiUsers /> }
  ];

  const handleBackClick = () => {
    window.location.hash = 'businesses';
  };

  const handleDeleteItem = (item) => {
    setDeleteItemConfirm(item);
  };


  const handleConfirmDeleteItem = async () => {
    if (!deleteItemConfirm) return;
    setDeleteItemLoading(true);
    // Derive business type from businessData (same logic as inside MenuTab)
    const bType = businessData?.business_info?.business_type || businessData?.business_type || '';
    try {
      let response;
      if (bType === 'R01' || bType === 'R08') {
        // Grocery / Fashion — use product delete endpoint
        response = await AdminService.deleteProduct(deleteItemConfirm.item_id);
      } else {
        // Restaurant (R02) — use existing menu item delete endpoint
        response = await AdminService.deleteMenuItem(businessId, deleteItemConfirm.item_id, deleteItemConfirm.item_type);
      }
      if (response.success || response.status === 'success' || (response.data && response.data.success !== false)) {
        message.success('Item deleted successfully');
        fetchMenuItems();
      } else {
        message.error('Error: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      message.error('Error deleting item: ' + error.message);
    } finally {
      setDeleteItemLoading(false);
      setDeleteItemConfirm(null);
    }
  };


  // Memoize offers and coupons to prevent unnecessary re-renders
  // MUST be before any conditional returns to follow Rules of Hooks
  const memoizedOffers = useMemo(() => businessData?.offers || [], [businessData?.offers]);
  const memoizedCoupons = useMemo(() => businessData?.coupons || [], [businessData?.coupons]);

  if (loading) {
    return (
      <div className="business-details-tabbed">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading business details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-details-tabbed">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={handleBackClick} className="btn-back">Go Back</button>
        </div>
      </div>
    );
  }

  const business = businessData?.business_info || {};
  const performance = performanceData || {};

  return (
    <div className="business-details-tabbed">
      {/* Fixed Header - Modern Compact Design */}
      <div className="business-header-fixed">
        {/* Left Section - Primary Business Info */}
        <div className="header-left">
          <div className="business-info-primary">
            <div className="business-title-row">
              <h1 className="business-name">{business.business_name}</h1>
              <span className="business-id">{business.business_id}</span>
            </div>

          </div>
        </div>

        {/* Right Section - Status Badges and Actions */}
        <div className="header-right">
          <span className={`status-pill ${business.is_verified ? 'verified' : 'pending'}`}>
            {business.is_verified ? 'Verified' : 'Pending'}
          </span>
          <span className={`status-pill ${business.status === 1 ? 'open' : 'closed'}`}>
            {business.status === 1 ? 'Open' : 'Closed'}
          </span>

          <button onClick={handleBackClick} className="btn-header btn-primary">Back</button>

        </div>
      </div>

      {/* Horizontal Tabs */}
      <div className="tabs-container">
        <div className="tabs-scroll">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab business={business} performance={performance} currentPeriod={currentPeriod} onPeriodChange={setCurrentPeriod} />}
        {activeTab === 'menu' && <MenuTab items={menuItems} businessId={businessId} businessData={businessData} onAddItem={fetchMenuItems} onDeleteItem={handleDeleteItem} cleanItemName={cleanItemName} isMobile={isMobile} isLoading={menuItemsLoading} onRefresh={fetchMenuItems} />}
        {activeTab === 'categories' && <CategoriesTab businessId={businessId} items={menuItems} />}
        {activeTab === 'orders' && <OrdersTab businessId={businessId} onRefresh={fetchBusinessData} isMobile={isMobile} businessType={businessData?.business_info?.business_type || businessData?.business_type || ''} />}
        {activeTab === 'reviews' && <ReviewsTab businessId={businessId} />}
        {activeTab === 'offers' && <OffersAndCouponsTab offers={memoizedOffers} coupons={memoizedCoupons} businessId={businessId} onRefresh={fetchBusinessData} setShowOfferModal={setShowOfferModal} setShowCouponModal={setShowCouponModal} setEditingOffer={setEditingOffer} setEditingCoupon={setEditingCoupon} showOfferModal={showOfferModal} showCouponModal={showCouponModal} />}
        {activeTab === 'staff' && <BusinessStaffManagement businessId={businessId} />}
      </div>


      {/* Delete Item Confirm Modal */}
      {deleteItemConfirm && (
        <div
          onClick={() => !deleteItemLoading && setDeleteItemConfirm(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 999999
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', padding: '32px',
              maxWidth: '420px', width: '90%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center'
            }}
          >
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#fee2e2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px'
            }}>
              <FiTrash2 size={24} color="#ef4444" />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
              Delete Menu Item?
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong>"{deleteItemConfirm.name}"</strong>?
            </p>
            <div style={{
              background: '#fefce8', border: '1px solid #fde047',
              borderRadius: '10px', padding: '14px 16px',
              marginBottom: '24px', textAlign: 'left'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#854d0e', lineHeight: '1.6' }}>
                ⚠️ <strong>Warning:</strong> This item may be linked to existing orders, cart items, or other records.
                Deleting it could cause errors or data inconsistencies in those areas.
                Proceed only if you are sure this item is no longer in use. <strong>May lead to app crash.</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteItemConfirm(null)}
                disabled={deleteItemLoading}
                style={{
                  padding: '10px 24px', border: '1px solid #d1d5db',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '500', minWidth: '100px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteItem}
                disabled={deleteItemLoading}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: '8px', background: deleteItemLoading ? '#9ca3af' : '#ef4444',
                  color: 'white', cursor: deleteItemLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '600', minWidth: '100px'
                }}
              >
                {deleteItemLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Working Offer Modal */}
      {showOfferModal && (
        <div
          id="offer-modal-overlay"
          onClick={(e) => {
            // Only close if clicking the overlay itself, not the modal content
            if (e.target.id === 'offer-modal-overlay') {
              setShowOfferModal(false);
              setEditingOffer(null);
            }
          }}
          style={{
            position: 'fixed',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }}
        >
          <div
            id="offer-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '700px',
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: '20px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>{editingOffer ? 'Edit Offer' : 'Add New Offer'}</h3>
              <button
                onClick={() => {
                  setShowOfferModal(false);
                  setEditingOffer(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                x
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();

              // Helper function to convert empty strings to null for numeric fields
              const getNumericValue = (value) => {
                return value === '' || value === null || value === undefined ? null : value;
              };

              // Helper function to format date for MySQL (YYYY-MM-DD only)
              const formatDate = (dateValue) => {
                if (!dateValue) return null;
                // Extract just the date part (YYYY-MM-DD) from any format
                const dateStr = String(dateValue);
                const match = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
                return match ? match[1] : null;
              };

              const offerData = {
                title: e.target.title.value,
                offer_type: 'DISCOUNT',
                description: e.target.description.value || '',
                discount_percentage: getNumericValue(e.target.discount_percentage.value),
                discount_amount: getNumericValue(e.target.discount_amount.value),
                original_price: getNumericValue(e.target.original_price.value),
                offer_price: getNumericValue(e.target.offer_price.value),
                is_active: e.target.is_active.checked,
                is_approved: e.target.is_approved.checked,
                valid_from: formatDate(e.target.valid_from.value),
                valid_to: formatDate(e.target.valid_to.value),
                priority: getNumericValue(e.target.priority.value) || 0,
                max_views: getNumericValue(e.target.max_views.value)
              };

              // Add offer_id if editing
              if (editingOffer) {
                offerData.offer_id = editingOffer.offer_id;
              }

              handleSaveOffer(offerData);
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Offer Title *</label>
                <input
                  type="text"
                  name="title"
                  defaultValue={editingOffer?.title || ''}
                  placeholder="e.g., Buy 1 Get 1 Free"
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Priority</label>
                <input
                  type="number"
                  name="priority"
                  defaultValue={editingOffer?.priority || 0}
                  placeholder="0"
                  min="0"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
                <textarea
                  name="description"
                  defaultValue={editingOffer?.description || ''}
                  placeholder="Offer description..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Discount Percentage (%)</label>
                  <input
                    type="number"
                    name="discount_percentage"
                    defaultValue={editingOffer?.discount_percentage || ''}
                    placeholder="e.g., 20"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Discount Amount (?)</label>
                  <input
                    type="number"
                    name="discount_amount"
                    defaultValue={editingOffer?.discount_amount || ''}
                    placeholder="e.g., 100"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Original Price (?)</label>
                  <input
                    type="number"
                    name="original_price"
                    defaultValue={editingOffer?.original_price || ''}
                    placeholder="e.g., 500"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Offer Price (?)</label>
                  <input
                    type="number"
                    name="offer_price"
                    defaultValue={editingOffer?.offer_price || ''}
                    placeholder="e.g., 400"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valid From</label>
                  <input
                    type="date"
                    name="valid_from"
                    defaultValue={editingOffer?.valid_from ? editingOffer.valid_from.split('T')[0] : ''}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valid Until</label>
                  <input
                    type="date"
                    name="valid_to"
                    defaultValue={editingOffer?.valid_to ? editingOffer.valid_to.split('T')[0] : ''}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Max Views</label>
                <input
                  type="number"
                  name="max_views"
                  defaultValue={editingOffer?.max_views || ''}
                  placeholder="Unlimited"
                  min="0"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    defaultChecked={editingOffer?.is_active !== undefined ? editingOffer.is_active : true}
                    style={{ width: 'auto' }}
                  />
                  Active
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    name="is_approved"
                    defaultChecked={editingOffer?.is_approved !== undefined ? editingOffer.is_approved : false}
                    style={{ width: 'auto' }}
                  />
                  Approved
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowOfferModal(false);
                    setEditingOffer(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingOffer}
                  style={{
                    padding: '10px 20px',
                    background: isSavingOffer ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSavingOffer ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSavingOffer ? 'Saving...' : (editingOffer ? 'Update Offer' : 'Create Offer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Working Coupon Modal */}
      {showCouponModal && (
        <div
          id="coupon-modal-overlay"
          onClick={(e) => {
            // Only close if clicking the overlay itself, not the modal content
            if (e.target.id === 'coupon-modal-overlay') {
              setShowCouponModal(false);
              setEditingCoupon(null);
            }
          }}
          style={{
            position: 'fixed',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }}
        >
          <div
            id="coupon-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '600px',
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: '20px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>{editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}</h3>
              <button
                onClick={() => {
                  setShowCouponModal(false);
                  setEditingCoupon(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                x
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleSaveCoupon({
                coupon_code: e.target.coupon_code.value,
                discount_type: e.target.discount_type.value,
                discount_value: e.target.discount_value.value,
                is_active: e.target.is_active.checked,
                valid_from: e.target.valid_from.value,
                valid_to: e.target.valid_to.value,
                max_usage_total: e.target.max_usage_total.value,
                min_order_value: e.target.min_order_value.value,
                max_discount_amount: e.target.max_discount_amount.value
              });
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Coupon Code *</label>
                <input
                  type="text"
                  name="coupon_code"
                  defaultValue={editingCoupon?.code || ''}
                  placeholder="e.g., SAVE20"
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Discount Type *</label>
                  <select
                    name="discount_type"
                    defaultValue={editingCoupon?.type || editingCoupon?.discount_type || 'percentage'}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="buy_one_get_one">Buy One Get One</option>
                    <option value="free_delivery">Free Delivery</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Discount Value *</label>
                  <input
                    type="number"
                    name="discount_value"
                    defaultValue={editingCoupon?.value || editingCoupon?.discount_value || ''}
                    placeholder={editingCoupon?.type === 'percentage' || editingCoupon?.discount_type === 'percentage' ? '10' : '100'}
                    required
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valid From</label>
                  <input
                    type="date"
                    name="valid_from"
                    defaultValue={editingCoupon?.valid_from ? editingCoupon.valid_from.split('T')[0] : ''}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valid Until</label>
                  <input
                    type="date"
                    name="valid_to"
                    defaultValue={editingCoupon?.valid_to ? editingCoupon.valid_to.split('T')[0] : ''}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Max Usage</label>
                  <input
                    type="number"
                    name="max_usage_total"
                    defaultValue={editingCoupon?.max_usage || ''}
                    placeholder="Unlimited"
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Min Order Value</label>
                  <input
                    type="number"
                    name="min_order_value"
                    defaultValue={editingCoupon?.min_order_value || ''}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Max Discount</label>
                  <input
                    type="number"
                    name="max_discount_amount"
                    defaultValue={editingCoupon?.max_discount_amount || ''}
                    placeholder="No limit"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    defaultChecked={editingCoupon?.is_active !== undefined ? editingCoupon.is_active : true}
                    style={{ width: 'auto' }}
                  />
                  Active
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCouponModal(false);
                    setEditingCoupon(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingOffer}
                  style={{
                    padding: '10px 20px',
                    background: isSavingOffer ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSavingOffer ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSavingOffer ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Overview Tab Component - Compact with Business Info + Graphs
const OverviewTab = ({ business, performance, currentPeriod, onPeriodChange }) => {
  const [peakHoursData, setPeakHoursData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [periodMetrics, setPeriodMetrics] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch data based on custom date range
  const fetchDataForPeriod = async (dateRange) => {
    if (!business.business_id) return;

    setLoading(true);
    try {
      // Handle custom date range
      let periodParam = dateRange;

      // If it's a custom date range, convert to API format
      if (dateRange.startDate && dateRange.endDate) {
        periodParam = {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          type: 'custom'
        };

        // Calculate days difference for metrics API
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

        // Fetch peak hours data with custom range
        const peakHoursResponse = await AdminService.getBusinessPeakHours(business.business_id, periodParam);
        if (peakHoursResponse.success) {
          setPeakHoursData(peakHoursResponse.data);
        }

        // Fetch order status distribution with custom range
        const orderStatusResponse = await AdminService.getBusinessOrderStatus(business.business_id, periodParam);
        if (orderStatusResponse.success) {
          setOrderStatusData(orderStatusResponse.data);
        }

        // Fetch period-specific metrics using days parameter
        const metricsResponse = await AdminService.getBusinessPerformanceMetrics(business.business_id, daysDiff, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        });

        if (metricsResponse.success) {
          setPeriodMetrics(metricsResponse.data || metricsResponse);
        } else {
          console.warn('Failed to fetch period metrics:', metricsResponse);
          setPeriodMetrics({});
        }
      } else {
        // Fallback to old logic if no custom range
        const days = 7; // default to 7 days

        const peakHoursResponse = await AdminService.getBusinessPeakHours(business.business_id, '7days');
        if (peakHoursResponse.success) {
          setPeakHoursData(peakHoursResponse.data);
        }

        const orderStatusResponse = await AdminService.getBusinessOrderStatus(business.business_id, '7days');
        if (orderStatusResponse.success) {
          setOrderStatusData(orderStatusResponse.data);
        }

        const metricsResponse = await AdminService.getBusinessPerformanceMetrics(business.business_id, days);

        if (metricsResponse.success) {
          setPeriodMetrics(metricsResponse.data || metricsResponse);
        } else {
          console.warn('Failed to fetch period metrics:', metricsResponse);
          setPeriodMetrics({});
        }
      }
    } catch (error) {
      console.error('Error fetching data for period:', error);
      setPeriodMetrics({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataForPeriod(currentPeriod);
  }, [business.business_id, currentPeriod]);

  // Handle period change
  const handlePeriodChange = (period) => {
    onPeriodChange(period);
  };

  // Calculate metrics - Use period-specific data when available, fallback to performance data
  let currentMetrics = performance; // Default to performance data

  // Extract data from the nested structure if period metrics are available
  if (periodMetrics && Object.keys(periodMetrics).length > 0) {
    const financial = periodMetrics.financial || {};
    const operational = periodMetrics.operational || {};
    const quality = periodMetrics.quality || {};
    const customers = periodMetrics.customers || {};



    // Log all values to find potential revenue fields
    Object.entries(financial).forEach(([key, value]) => {
    });
    Object.entries(operational).forEach(([key, value]) => {
    });

    // Build metrics object from nested data with comprehensive property checking
    const revenueValue =
      // Financial object properties
      financial.total_revenue || financial.revenue || financial.total_sales || financial.sales ||
      financial.gross_revenue || financial.net_revenue || financial.total_amount || financial.amount ||
      financial.revenue_total || financial.sales_total || financial.earnings || financial.income ||
      financial.total_earning || financial.total_income || financial.gross_sales || financial.net_sales ||
      // Operational object properties  
      operational.total_revenue || operational.revenue || operational.total_sales || operational.sales ||
      operational.amount || operational.total_amount || operational.earnings || operational.income ||
      operational.total_earning || operational.total_income || operational.gross_sales || operational.net_sales ||
      // Quality object properties
      quality.total_revenue || quality.revenue || quality.total_sales || quality.sales ||
      // Customers object properties
      customers.total_revenue || customers.revenue || customers.total_sales || customers.sales ||
      // Check for nested revenue objects
      (financial.revenue_data && financial.revenue_data.total) ||
      (operational.revenue_data && operational.revenue_data.total) ||
      // Check for any large numeric value that might be revenue (> 100)
      Object.values(financial).find(val => typeof val === 'number' && val > 100) ||
      Object.values(operational).find(val => typeof val === 'number' && val > 100) ||
      0;

    currentMetrics = {
      total_orders:
        financial.total_orders || operational.total_orders || financial.orders || operational.orders ||
        financial.order_count || operational.order_count || financial.num_orders || operational.num_orders || 0,
      total_revenue: revenueValue,
      completed_orders:
        operational.completed_orders || operational.delivered_orders || operational.successful_orders ||
        financial.completed_orders || operational.complete_orders || operational.fulfilled_orders ||
        operational.success_orders || financial.delivered_orders || 0,
      avg_order_value:
        financial.avg_order_value || financial.average_order_value || financial.aov ||
        operational.avg_order_value || operational.average_order_value || operational.aov ||
        financial.mean_order_value || operational.mean_order_value || 0,
      avg_rating:
        quality.avg_rating || quality.average_rating || quality.rating || quality.mean_rating ||
        customers.avg_rating || customers.average_rating || customers.rating ||
        performance.avg_rating || 0,
      total_reviews:
        quality.total_reviews || quality.reviews || quality.review_count || quality.num_reviews ||
        customers.total_reviews || customers.reviews || customers.review_count || 0,
      completion_rate:
        operational.completion_rate || operational.success_rate || operational.delivery_rate ||
        operational.fulfillment_rate || operational.order_completion_rate || operational.complete_rate ||
        financial.completion_rate || financial.success_rate || 0
    };

    // Convert revenue to number if it's a string
    if (typeof currentMetrics.total_revenue === 'string') {
      currentMetrics.total_revenue = parseFloat(currentMetrics.total_revenue) || 0;
    }

  }


  // Safely extract values with fallbacks
  const totalOrders = currentMetrics.total_orders || 0;
  const totalRevenue = currentMetrics.total_revenue || 0;
  const avgOrderValue = currentMetrics.avg_order_value || 0;
  const avgRating = currentMetrics.avg_rating || 0;
  const totalReviews = currentMetrics.total_reviews || 0;

  const completionRate = currentMetrics.completion_rate ?
    parseFloat(currentMetrics.completion_rate).toFixed(2) :
    (totalOrders > 0 ? ((currentMetrics.completed_orders || 0) / totalOrders * 100).toFixed(2) : '0.00');

  // Get period-specific labels for custom date range
  const getPeriodLabel = (metric) => {
    if (currentPeriod.startDate && currentPeriod.endDate) {
      const startDate = new Date(currentPeriod.startDate).toLocaleDateString();
      const endDate = new Date(currentPeriod.endDate).toLocaleDateString();
      return `${metric} (${startDate} - ${endDate})`;
    }
    return metric;
  };

  return (
    <div className="tab-overview-compact-new">
      {/* Custom Date Range Filter Controls */}
      <div className="period-filter-controls">
        <div className="date-range-filter" style={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
          <div className="date-input-group">
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              From:
            </label>
            <input
              type="date"
              value={currentPeriod.startDate || ''}
              onChange={(e) => handlePeriodChange({ ...currentPeriod, startDate: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              className="date-input"
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div className="date-input-group">
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              To:
            </label>
            <input
              type="date"
              value={currentPeriod.endDate || ''}
              onChange={(e) => handlePeriodChange({ ...currentPeriod, endDate: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              min={currentPeriod.startDate || ''}
              className="date-input"
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>
        {loading && <div className="loading-indicator" style={{ marginLeft: '16px', fontSize: '14px', color: '#6b7280' }}>Loading...</div>}
      </div>

      {/* Period Info Banner - Removed since info is now in filter controls */}

      {/* Top Row: Business Info + Address + Status */}
      <div className="overview-info-row">
        {/* Business Info Card */}
        <div className="info-card-compact-new">
          <h3>Business Information</h3>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Business Name:</span>
            <span className="value-compact-new">{business.business_name}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Owner:</span>
            <span className="value-compact-new">{business.owner_first_name} {business.owner_last_name}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Phone:</span>
            <span className="value-compact-new">{business.phone}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Email:</span>
            <span className="value-compact-new">{business.email}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">GST:</span>
            <span className="value-compact-new">{business.gst_number || business.gstin || 'N/A'}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">FSSAI:</span>
            <span className="value-compact-new">{business.fssai_number || 'N/A'}</span>
          </div>
        </div>

        {/* Address Card */}
        <div className="info-card-compact-new">
          <h3>Address</h3>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Address:</span>
            <span className="value-compact-new">{business.address}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">City:</span>
            <span className="value-compact-new">{business.city}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">State:</span>
            <span className="value-compact-new">{business.state}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Pincode:</span>
            <span className="value-compact-new">{business.pincode}</span>
          </div>
          <div className="map-view-compact-new">
            <a
              href={
                business.latitude && business.longitude
                  ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    business.business_name || business.businessName || 'Business'
                  )}+${encodeURIComponent(
                    [business.address, business.city, business.state, business.pincode]
                      .filter(Boolean)
                      .join(', ')
                  )}&center=${business.latitude},${business.longitude}&zoom=15`
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [
                      business.business_name || business.businessName || 'Business',
                      business.address,
                      business.city,
                      business.state,
                      business.pincode
                    ]
                      .filter(Boolean)
                      .join(', ')
                  )}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="btn-map-compact-new"
            >
              <FiMapPin /> View on Map
            </a>
          </div>
        </div>

        {/* Business Status Card */}
        <div className="info-card-compact-new">
          <h3>Business Status</h3>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Status:</span>
            <span className="value-compact-new">
              <span className={`badge ${business.status === 1 ? 'badge-success' : 'badge-danger'}`}>
                {business.status === 1 ? 'Active' : 'Inactive'}
              </span>
            </span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Verified:</span>
            <span className="value-compact-new">
              <span className={`badge ${business.is_verified ? 'badge-success' : 'badge-warning'}`}>
                {business.is_verified ? 'Verified' : 'Pending'}
              </span>
            </span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Type:</span>
            <span className="value-compact-new">{business.business_type || 'Restaurant'}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Category:</span>
            <span className="value-compact-new">{business.business_category || 'N/A'}</span>
          </div>
          <div className="info-row-compact-new">
            <span className="label-compact-new">Joined:</span>
            <span className="value-compact-new">{business.created_at ? new Date(business.created_at).toLocaleDateString() : 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Key Metrics Row - Dynamic Period */}
      <div className="metrics-row-compact-new">
        <div className="metric-card-compact-new">
          <div className="metric-icon-compact-new orders"><MdOutlineShoppingCart /></div>
          <div className="metric-content-compact-new">
            <div className="metric-value-compact-new">{totalOrders}</div>
            <div className="metric-label-compact-new">
              {getPeriodLabel('Orders')}
            </div>
          </div>
        </div>

        <div className="metric-card-compact-new">
          <div className="metric-icon-compact-new revenue"><MdOutlineCurrencyRupee /></div>
          <div className="metric-content-compact-new">
            <div className="metric-value-compact-new">{Math.round(totalRevenue).toLocaleString()}</div>
            <div className="metric-label-compact-new">
              {getPeriodLabel('Revenue')}
            </div>
          </div>
        </div>

        <div className="metric-card-compact-new">
          <div className="metric-icon-compact-new completion"><MdOutlineCheckCircleOutline /></div>
          <div className="metric-content-compact-new">
            <div className="metric-value-compact-new">{completionRate}%</div>
            <div className="metric-label-compact-new">
              {getPeriodLabel('Completion Rate')}
            </div>
          </div>
        </div>

        <div className="metric-card-compact-new">
          <div className="metric-icon-compact-new"><MdOutlineBarChart /></div>
          <div className="metric-content-compact-new">
            <div className="metric-value-compact-new">{avgOrderValue.toFixed(0)}</div>
            <div className="metric-label-compact-new">
              {getPeriodLabel('Avg Order Value')}
            </div>
          </div>
        </div>

        <div className="metric-card-compact-new">
          <div className="metric-icon-compact-new rating"><MdOutlineStarOutline /></div>
          <div className="metric-content-compact-new">
            <div className="metric-value-compact-new">{avgRating.toFixed(1)}</div>
            <div className="metric-label-compact-new">Avg Rating</div>
          </div>
        </div>

        <div className="metric-card-compact-new">
          <div className="metric-icon-compact-new"><MdOutlineMessage /></div>
          <div className="metric-content-compact-new">
            <div className="metric-value-compact-new">{totalReviews}</div>
            <div className="metric-label-compact-new">Total Reviews</div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row-compact-new">
        {/* Peak Hours Chart */}
        <div className="chart-card-compact-new">
          <h3 className="chart-title-compact-new">Peak Hours Analysis</h3>
          <PeakHoursChart data={peakHoursData} period={currentPeriod} />
        </div>

        {/* Order Status Distribution */}
        <div className="chart-card-compact-new">
          <h3 className="chart-title-compact-new">Order Status Distribution</h3>
          <OrderStatusChart
            data={orderStatusData}
            performance={performance}
            currentPeriod={currentPeriod}
            periodMetrics={periodMetrics}
          />
        </div>
      </div>
    </div>
  );
};

// Complete Histogram Component - Top 3 Peak Hours Only
const PeakHoursChart = ({ data, period }) => {
  const allData = data && data.length > 0 ? data : [];

  // Sort by orders descending, take top 3
  const topPeakHours = [...allData]
    .sort((a, b) => (b.order_count || b.orders || 0) - (a.order_count || a.orders || 0))
    .slice(0, 3);

  const maxOrders = Math.max(...topPeakHours.map(item => item.order_count || item.orders || 0), 1);
  const chartHeight = 200;

  const rankColors = ['#fbbf24', '#9ca3af', '#f97316']; // gold, silver, bronze
  const rankLabels = ['', '', ''];

  const getPeriodLabel = (period) => {
    if (period && period.startDate && period.endDate) {
      return `${new Date(period.startDate).toLocaleDateString()} - ${new Date(period.endDate).toLocaleDateString()}`;
    }
    switch (period) {
      case 'today': return 'Today';
      case '7days': return 'Last 7 Days';
      case 'month': return 'Last 30 Days';
      case 'year': return 'Last Year';
      default: return 'Last 7 Days';
    }
  };

  // Y-axis ticks: 0 at top ? maxOrders at bottom (bars grow upward from baseline)
  const yTicks = Array.from({ length: 6 }, (_, i) => Math.round(maxOrders * i / 5)).reverse();

  return (
    <div className="complete-histogram">

      {topPeakHours.length === 0 ? (
        <div className="no-data-message">
          <p>No peak hours data available</p>
        </div>
      ) : (
        <div className="histogram-full-wrapper">
          {/* Y-axis */}
          <div className="y-axis-full">
            {yTicks.map((val, i) => (
              <div key={i} className="y-tick-full">{val}</div>
            ))}
          </div>

          {/* Chart area */}
          <div className="chart-area-full">
            <div className="bars-container-full" style={{ height: `${chartHeight}px` }}>
              {topPeakHours.map((hourData, index) => {
                const count = hourData.order_count || hourData.orders || 0;
                const barHeight = maxOrders > 0 ? (count / maxOrders) * chartHeight : 0;
                return (
                  <div key={hourData.hour || index} className="bar-wrapper-full">
                    <div
                      className="histogram-bar-full"
                      style={{ height: `${barHeight}px`, backgroundColor: rankColors[index] }}
                      title={`${hourData.time_label}: ${count} orders`}
                    >
                      {count > 0 && (
                        <span className="bar-value-full">{count}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="x-axis-line-full" />

            <div className="x-labels-full">
              {topPeakHours.map((hourData, index) => (
                <div key={hourData.hour || index} className="x-label-full">
                  <div className="time-text-full">
                    {rankLabels[index]} {hourData.time_label || hourData.hour || `${hourData.hour}:00`}
                  </div>
                  <div className="x-axis-value-full">
                    {hourData.order_count || hourData.orders || 0} orders
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Order Status Chart Component
const OrderStatusChart = ({ data, performance, currentPeriod, periodMetrics }) => {
  let statuses;

  // Priority 1: dedicated order-status API response (flat object from /order-status/ endpoint)
  if (data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0) {
    statuses = [
      { label: 'Delivered', count: data.completed_orders || data.delivered_orders || data.successful_orders || 0, color: '#10b981' },
      { label: 'Pending', count: data.pending_orders || data.pending || 0, color: '#f59e0b' },
      { label: 'Cancelled', count: data.cancelled_orders || data.canceled_orders || data.cancelled || 0, color: '#ef4444' },
      { label: 'Processing', count: data.processing_orders || data.processing || data.in_progress_orders || 0, color: '#3b82f6' },
    ];
  }
  // Priority 2: array format from API
  else if (data && Array.isArray(data) && data.length > 0) {
    const find = (keys) => {
      const item = data.find(d => keys.includes(d.status) || keys.includes(d.label));
      return item?.count || item?.value || 0;
    };
    statuses = [
      { label: 'Delivered', count: find(['delivered', 'completed', 'Delivered']), color: '#10b981' },
      { label: 'Pending', count: find(['pending', 'Pending']), color: '#f59e0b' },
      { label: 'Cancelled', count: find(['cancelled', 'canceled', 'Cancelled']), color: '#ef4444' },
      { label: 'Processing', count: find(['processing', 'Processing']), color: '#3b82f6' },
    ];
  }
  // Priority 3: nested periodMetrics (from performance metrics API)
  else if (periodMetrics && Object.keys(periodMetrics).length > 0) {
    const op = periodMetrics.operational || {};
    const fin = periodMetrics.financial || {};
    statuses = [
      { label: 'Delivered', count: op.completed_orders || op.delivered_orders || op.successful_orders || fin.completed_orders || 0, color: '#10b981' },
      { label: 'Pending', count: op.pending_orders || op.pending || op.awaiting_orders || fin.pending_orders || 0, color: '#f59e0b' },
      { label: 'Cancelled', count: op.cancelled_orders || op.canceled_orders || op.cancelled || fin.cancelled_orders || 0, color: '#ef4444' },
      { label: 'Processing', count: op.processing_orders || op.processing || op.in_progress_orders || fin.processing_orders || 0, color: '#3b82f6' },
    ];
  }
  // Priority 4: performance prop fallback
  else {
    statuses = [
      { label: 'Delivered', count: performance.completed_orders || performance.delivered_orders || 0, color: '#10b981' },
      { label: 'Pending', count: performance.pending_orders || performance.pending || 0, color: '#f59e0b' },
      { label: 'Cancelled', count: performance.cancelled_orders || performance.canceled_orders || 0, color: '#ef4444' },
      { label: 'Processing', count: performance.processing_orders || performance.processing || 0, color: '#3b82f6' },
    ];
  }

  const total = statuses.reduce((sum, status) => sum + status.count, 0);

  return (
    <div className="order-status-chart-compact">
      <div className="status-list-compact">
        {statuses.map(status => {
          const percentage = total > 0 ? ((status.count / total) * 100).toFixed(1) : 0;
          return (
            <div key={status.label} className="status-item-compact">
              <div className="status-info-compact">
                <div className="status-color-compact" style={{ backgroundColor: status.color }}></div>
                <span className="status-label-compact">{status.label}</span>
              </div>
              <div className="status-stats-compact">
                <span className="status-count-compact">{status.count}</span>
                <span className="status-percentage-compact">({percentage}%)</span>
              </div>
              <div className="status-bar-bg-compact">
                <div
                  className="status-bar-fill-compact"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: status.color
                  }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Reusable Pagination Component
const PaginationBar = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const btnStyle = (active, disabled) => ({
    width: '44px', height: '44px', borderRadius: '10px',
    border: '1px solid ' + (active ? '#F55D00' : '#e5e7eb'),
    background: active ? '#F55D00' : 'white',
    color: active ? 'white' : disabled ? '#d1d5db' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '15px', fontWeight: active ? '700' : '500',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: active ? '0 4px 12px rgba(245,93,0,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
    transition: 'all 0.15s ease', flexShrink: 0
  });

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '20px 0 8px' }}>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={btnStyle(false, currentPage === 1)}>
        <MdOutlineChevronLeft size={18} />
      </button>
      {pages.map((item, idx) =>
        item === '...' ? (
          <span key={`e-${idx}`} style={{ width: '44px', textAlign: 'center', color: '#9ca3af', fontSize: '15px', letterSpacing: '2px' }}>...</span>
        ) : (
          <button key={item} onClick={() => onPageChange(item)} style={btnStyle(currentPage === item, false)}>{item}</button>
        )
      )}
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={btnStyle(false, currentPage === totalPages)}>
        <MdOutlineChevronRight size={18} />
      </button>
    </div>
  );
};

// ─── Product Details Modal ─────────────────────────────────────────────────
const ProductDetailsModal = ({ item, onClose }) => {
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  if (!item) return null;

  const raw = item._raw || {};
  const images = item.images && item.images.length ? item.images : (item.image ? [item.image] : []);
  const variants = item.variants || [];

  const stockColor = (s) => s === 'low_stock' ? '#f59e0b' : s === 'out_of_stock' ? '#ef4444' : '#10b981';

  const InfoCell = ({ label, value, accent }) => {
    let displayVal = '—';
    if (value != null && value !== '') {
      if (typeof value === 'object') {
        displayVal = value.size_label || value.value || value.label || JSON.stringify(value);
      } else {
        displayVal = String(value);
      }
    }
    return (
      <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 14px', borderLeft: accent ? '3px solid ' + accent : 'none' }}>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{displayVal}</div>
      </div>
    );
  };

  const SectionTitle = ({ children }) => (
    <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', paddingBottom: '6px', borderBottom: '2px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px' }}>
      {children}
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'variants', label: `Variants (${variants.length})` },
    { id: 'images', label: `Images (${images.length})` },
    { id: 'extra', label: 'Extra Info' },
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      justifyContent: 'center', alignItems: 'flex-start',
      zIndex: 99999, overflowY: 'auto', padding: '20px 12px'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '900px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column'
      }} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6', gap: '12px'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>{item.name}</h2>
              <span className={`badge ${item.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                {item.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              {item.is_new && <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#d1fae5', color: '#065f46' }}>NEW</span>}
              {item.is_bestseller && <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#dbeafe', color: '#1e40af' }}>BESTSELLER</span>}
            </div>
            <div style={{ marginTop: '6px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {item.category && <span style={{ fontSize: '13px', color: '#6b7280' }}>📁 {item.category}</span>}
              <span style={{ fontSize: '13px', color: '#6b7280' }}>ID: {item.item_id}</span>
              {item.rating > 0 && <span style={{ fontSize: '13px', color: '#f59e0b' }}>★ {item.rating} ({item.rating_count} reviews)</span>}
            </div>
            {/* Badges row */}
            {item.badges && item.badges.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {item.badges.map((b, i) => (
                  <span key={i} style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                    background: b.badge_type === 'discount' ? '#fef3c7' : '#dbeafe',
                    color: b.badge_type === 'discount' ? '#92400e' : '#1e40af'
                  }}>{b.badge_text}</span>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: '#f3f4f6', border: 'none', borderRadius: '8px',
            width: '32px', height: '32px', fontSize: '18px', cursor: 'pointer',
            color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>×</button>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '0 24px' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: activeTab === t.id ? '700' : '500',
              color: activeTab === t.id ? '#3b82f6' : '#6b7280',
              borderBottom: activeTab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-1px', whiteSpace: 'nowrap'
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: 'calc(90vh - 200px)' }}>

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Top: thumbnail + price/stock summary */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {images.length > 0 && (
                  <div style={{ flexShrink: 0, cursor: 'pointer' }} onClick={() => setActiveTab('images')}>
                    <img src={images[activeImg]} alt={item.name} style={{
                      width: '140px', height: '140px', objectFit: 'cover',
                      borderRadius: '10px', border: '1px solid #e5e7eb', display: 'block'
                    }} onError={e => { e.target.style.background = '#f3f4f6'; e.target.style.display = 'none'; }} />
                    {images.length > 1 && <div style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>+{images.length - 1} more</div>}
                  </div>
                )}
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', alignContent: 'start' }}>
                  <InfoCell label="Selling Price" value={item.price ? `₹${item.price}` : null} accent="#10b981" />
                  <InfoCell label="Original Cost" value={item.original_cost ? `₹${item.original_cost}` : null} />
                  <InfoCell label="Stock Qty" value={item.quantity} accent={item.stock_status === 'low_stock' ? '#f59e0b' : null} />
                  <InfoCell label="Availability" value={item.availability_status} />
                  <InfoCell label="Discount" value={item.discount_percentage ? `${item.discount_percentage}%` : null} accent={item.discount_percentage > 0 ? '#f59e0b' : null} />
                  <InfoCell label="Diff Amount" value={raw.diff_amount ? `₹${raw.diff_amount}` : null} />
                </div>
              </div>

              {/* Description */}
              {item.description && (
                <div>
                  <SectionTitle>Description</SectionTitle>
                  <p style={{ margin: 0, fontSize: '13px', color: '#4b5563', lineHeight: '1.7', background: '#f9fafb', padding: '12px 14px', borderRadius: '8px' }}>{item.description}</p>
                </div>
              )}

              {/* Product details grid */}
              <div>
                <SectionTitle>Product Details</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
                  <InfoCell label="Item ID" value={item.item_id} />
                  <InfoCell label="Category" value={item.category || raw.item_category} />
                  <InfoCell label="Category ID" value={raw.category_id} />
                  <InfoCell label="Sub Category" value={raw.sub_category_name || raw.sub_category} />
                  <InfoCell label="Sub Category ID" value={raw.sub_category_id || raw.sub_Category_id} />
                  <InfoCell label="Parent Category" value={raw.parent_category_name} />
                  <InfoCell label="Item Type" value={raw.item_type} />
                  <InfoCell label="Prep Time" value={item.preparation_time} />
                  <InfoCell label="Charges" value={raw.charges ? `₹${raw.charges}` : null} />
                  <InfoCell label="Size Label" value={raw.size_label} />
                  <InfoCell label="MRP" value={raw.mrp && parseFloat(raw.mrp) > 0 ? `₹${raw.mrp}` : null} />
                  <InfoCell label="Stock Status" value={item.stock_status} accent={stockColor(item.stock_status)} />
                  <InfoCell label="Customizable" value={raw.is_customizable ? 'Yes' : 'No'} />
                  <InfoCell label="Can Add to Cart" value={raw.can_add_to_cart ? 'Yes' : 'No'} />
                  <InfoCell label="Is New" value={item.is_new ? 'Yes' : 'No'} />
                  <InfoCell label="Bestseller" value={item.is_bestseller ? 'Yes' : 'No'} />
                  <InfoCell label="Featured Offer" value={raw.is_featured_offer ? 'Yes' : 'No'} />
                  <InfoCell label="Wishlist" value={raw.is_added_to_wishlist ? 'Yes' : 'No'} />
                  <InfoCell label="Business Open" value={raw.is_business_open ? 'Yes' : 'No'} />
                  <InfoCell label="Availability Timings" value={raw.availability_timings} />
                  <InfoCell label="Created At" value={raw.created_at} />
                  <InfoCell label="Updated At" value={raw.updated_at} />
                </div>
              </div>

              {/* Business features */}
              {raw.business_features && raw.business_features.length > 0 && (
                <div>
                  <SectionTitle>Business Features</SectionTitle>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {raw.business_features.map((f, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', background: '#e0e7ff', color: '#3730a3' }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VARIANTS TAB */}
          {activeTab === 'variants' && (
            <div>
              {variants.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No variants found</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {variants.map((v, idx) => (
                    <div key={v.variant_id} style={{
                      border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
                    }}>
                      {/* Variant header */}
                      <div style={{
                        background: '#f9fafb', padding: '10px 16px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontWeight: '700', color: '#374151', fontSize: '14px' }}>Variant #{idx + 1}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>ID: {v.variant_id}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px', background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: '4px' }}>{v.sku || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                            background: v.is_active ? '#d1fae5' : '#fee2e2',
                            color: v.is_active ? '#065f46' : '#991b1b'
                          }}>{v.is_active ? 'Active' : 'Inactive'}</span>
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                            background: v.availability_status === 'AVAILABLE' ? '#d1fae5' : '#fee2e2',
                            color: v.availability_status === 'AVAILABLE' ? '#065f46' : '#991b1b'
                          }}>{v.availability_status || '—'}</span>
                        </div>
                      </div>

                      {/* Variant details grid */}
                      <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                        <InfoCell label="Selling Price" value={v.selling_price ? `₹${v.selling_price}` : null} accent="#10b981" />
                        <InfoCell label="Original Cost" value={v.original_cost ? `₹${v.original_cost}` : null} />
                        <InfoCell label="MRP" value={parseFloat(v.mrp) > 0 ? `₹${v.mrp}` : null} />
                        <InfoCell label="Size Label" value={v.size_label} />
                        <InfoCell label="Stock Qty" value={v.stock_quantity != null ? v.stock_quantity : v.stock_qty}
                          accent={stockColor(v.stock_status)} />
                        <InfoCell label="Stock Status" value={v.stock_status} accent={stockColor(v.stock_status)} />
                        <InfoCell label="Stock Message" value={v.stock_message} />
                        <InfoCell label="Rating" value={v.rating != null ? `${v.rating} ★ (${v.rating_count})` : null} />
                        <InfoCell label="Can Add to Cart" value={v.can_add_to_cart ? 'Yes' : 'No'} />
                        <InfoCell label="GST %" value={v.gst_percentage != null ? `${v.gst_percentage}%` : null} />
                        <InfoCell label="Avail. Message" value={v.availability_message} />
                      </div>

                      {/* GST details sub-section */}
                      {v.gst_details && v.gst_details.tax_name && (
                        <div style={{ padding: '0 16px 14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GST Details</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                            <InfoCell label="Tax Name" value={v.gst_details.tax_name} accent="#3b82f6" />
                            <InfoCell label="GST %" value={v.gst_details.gst_percentage != null ? `${v.gst_details.gst_percentage}%` : null} />
                            <InfoCell label="CGST %" value={v.gst_details.cgst_percentage != null ? `${v.gst_details.cgst_percentage}%` : null} />
                            <InfoCell label="SGST %" value={v.gst_details.sgst_percentage != null ? `${v.gst_details.sgst_percentage}%` : null} />
                            <InfoCell label="IGST %" value={v.gst_details.igst_percentage != null ? `${v.gst_details.igst_percentage}%` : null} />
                            <InfoCell label="UGST %" value={v.gst_details.ugst_percentage != null ? `${v.gst_details.ugst_percentage}%` : null} />
                            <InfoCell label="Tax Type" value={v.gst_details.tax_type} />
                            <InfoCell label="GST ID" value={v.gst_details.gst_id} />
                            <InfoCell label="Tax ID" value={v.gst_details.tax_id} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* IMAGES TAB */}
          {activeTab === 'images' && (
            <div>
              {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No images available</div>
              ) : (
                <div>
                  {/* Main preview */}
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <img src={images[activeImg]} alt={item.name} style={{
                      maxWidth: '100%', maxHeight: '360px', objectFit: 'contain',
                      borderRadius: '12px', border: '1px solid #e5e7eb'
                    }} onError={e => { e.target.style.display = 'none'; }} />
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                      Image {activeImg + 1} of {images.length}
                    </div>
                  </div>
                  {/* Thumbnails */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {images.map((img, i) => (
                      <div key={i} onClick={() => setActiveImg(i)} style={{
                        cursor: 'pointer', border: i === activeImg ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                        borderRadius: '8px', overflow: 'hidden', flexShrink: 0,
                        boxShadow: i === activeImg ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none'
                      }}>
                        <img src={img} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', display: 'block' }}
                          onError={e => { e.target.style.display = 'none'; }} />
                      </div>
                    ))}
                  </div>
                  {/* URLs list */}
                  <div style={{ marginTop: '20px' }}>
                    <SectionTitle>Image URLs</SectionTitle>
                    {images.map((img, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', flexShrink: 0 }}>#{i + 1}</span>
                        <span style={{ fontSize: '11px', color: '#374151', wordBreak: 'break-all', flex: 1, fontFamily: 'monospace' }}>{img}</span>
                        <a href={img} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#3b82f6', flexShrink: 0 }}>Open</a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EXTRA INFO TAB */}
          {activeTab === 'extra' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Flags */}
              <div>
                <SectionTitle>Flags & Status</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  <InfoCell label="Is Active" value={raw.is_active ? 'Yes' : 'No'} accent={raw.is_active ? '#10b981' : '#ef4444'} />
                  <InfoCell label="Status" value={raw.status ? 'Visible' : 'Hidden'} />
                  <InfoCell label="Is New" value={item.is_new ? 'Yes' : 'No'} />
                  <InfoCell label="Bestseller" value={item.is_bestseller ? 'Yes' : 'No'} />
                  <InfoCell label="Featured Offer" value={raw.is_featured_offer ? 'Yes' : 'No'} />
                  <InfoCell label="Customizable" value={raw.is_customizable ? 'Yes' : 'No'} />
                  <InfoCell label="Can Add to Cart" value={raw.can_add_to_cart ? 'Yes' : 'No'} />
                  <InfoCell label="Business Open" value={raw.is_business_open ? 'Yes' : 'No'} />
                  <InfoCell label="In Wishlist" value={raw.is_added_to_wishlist ? 'Yes' : 'No'} />
                </div>
              </div>

              {/* Pricing extras */}
              <div>
                <SectionTitle>Pricing & Discount</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  <InfoCell label="Selling Price" value={raw.selling_price ? `₹${raw.selling_price}` : null} accent="#10b981" />
                  <InfoCell label="Original Cost" value={raw.original_cost ? `₹${raw.original_cost}` : null} />
                  <InfoCell label="MRP" value={raw.mrp && parseFloat(raw.mrp) > 0 ? `₹${raw.mrp}` : null} />
                  <InfoCell label="Diff Amount" value={raw.diff_amount ? `₹${raw.diff_amount}` : null} />
                  <InfoCell label="Discount %" value={raw.discount_percentage ? `${raw.discount_percentage}%` : null} accent={raw.discount_percentage > 0 ? '#f59e0b' : null} />
                  <InfoCell label="Percent Display" value={raw.percent_display != null ? `${raw.percent_display}%` : null} />
                  <InfoCell label="Diff Display" value={raw.diff_display} />
                  <InfoCell label="Charges" value={raw.charges ? `₹${raw.charges}` : null} />
                </div>
              </div>

              {/* Stock & availability */}
              <div>
                <SectionTitle>Stock & Availability</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  <InfoCell label="Stock Quantity" value={raw.stock_quantity} accent={stockColor(raw.stock_status)} />
                  <InfoCell label="Stock Status" value={raw.stock_status} accent={stockColor(raw.stock_status)} />
                  <InfoCell label="Stock Message" value={raw.stock_message} />
                  <InfoCell label="Availability Status" value={raw.availability_status} />
                  <InfoCell label="Avail. Message" value={raw.availability_message} />
                  <InfoCell label="Avail. Timings" value={raw.availability_timings} />
                </div>
              </div>

              {/* Ratings */}
              <div>
                <SectionTitle>Ratings</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  <InfoCell label="Rating" value={raw.rating != null ? `${raw.rating} ★` : null} accent="#f59e0b" />
                  <InfoCell label="Rating Count" value={raw.rating_count} />
                </div>
              </div>

              {/* Timestamps */}
              <div>
                <SectionTitle>Timestamps</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  <InfoCell label="Created At" value={raw.created_at} />
                  <InfoCell label="Updated At" value={raw.updated_at} />
                  <InfoCell label="Item Placed At" value={raw.item_placed_at} />
                </div>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
};


// --- Menu Tab Component ----------------------------------------------------
const MenuTab = ({ items, businessId, businessData, onAddItem, onEditItem, onDeleteItem, cleanItemName, isMobile, isLoading, onRefresh }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewItem, setViewItem] = useState(null);
  const [itemsState, setItemsState] = useState({});  // local overrides for status
  const [togglingItem, setTogglingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);
  const [confirmToggleModal, setConfirmToggleModal] = useState({ show: false, item: null });
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const itemsPerPage = 10;

  const handleAddItemSubmit = async (formDataPayload) => {
    setIsAddingItem(true);
    try {
      const res = await AdminService.addMenuItem(businessId, formDataPayload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Menu item added successfully');
        setShowAddModal(false);
        if (onAddItem) onAddItem();
      } else {
        message.error('Failed to add item: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error adding item: ' + err.message);
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleAddProductSubmit = async (formDataPayload) => {
    setIsAddingProduct(true);
    try {
      const res = await AdminService.addMenuItem(businessId, formDataPayload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Product added successfully');
        setShowAddProductModal(false);
        if (onAddItem) onAddItem();
      } else {
        message.error('Failed to add product: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error adding product: ' + err.message);
    } finally {
      setIsAddingProduct(false);
    }
  };

  const handleUpdateItemSubmit = async (formDataPayload) => {
    setIsUpdatingItem(true);
    try {
      const res = await AdminService.updateMenuItem(businessId, formDataPayload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Menu item updated successfully');
        setEditingMenuId(null);
        if (onAddItem) onAddItem(); // refresh list
      } else {
        message.error('Failed to update item: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error updating item: ' + err.message);
    } finally {
      setIsUpdatingItem(false);
    }
  };

  const businessType = businessData && businessData.business_info
    ? businessData.business_info.business_type
    : (businessData ? businessData.business_type : '');

  const categories = [...new Set(items.map(item => item.category).filter(Boolean))];

  const filteredItems = items.filter(item => {
    const statusMatch = statusFilter === 'all' || item.status === statusFilter;
    const categoryMatch = categoryFilter === 'all' || item.category === categoryFilter;
    const searchMatch = searchTerm === '' || item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return statusMatch && categoryMatch && searchMatch;
  });

  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [statusFilter, categoryFilter, searchTerm]);

  const handlePageChange = (page) => { setCurrentPage(page); };

  const getItemStatus = (item) => itemsState[item.item_id]?.status ?? item.status;

  const handleToggleStatus = (item) => {
    setConfirmToggleModal({ show: true, item });
  };

  const handleConfirmToggleStatus = async () => {
    if (!confirmToggleModal.item) return;
    const item = confirmToggleModal.item;
    const currentStatus = getItemStatus(item);
    const newIsActive = currentStatus !== 'active';
    setTogglingItem(item.item_id);
    setConfirmToggleModal({ show: false, item: null });

    try {
      let response;

      if (businessType === 'R01' || businessType === 'R08') {
        // Grocery / Fashion — use product status endpoint (soft delete / restore)
        response = await AdminService.updateProductStatus(item.item_id, newIsActive);
      } else {
        // Restaurant (R02) — use existing menu-status endpoint (PATCH)
        response = await AdminService.updateMenuItemStatus(businessId, item.item_id, newIsActive);
      }

      if (response.success || response.status === 'success' || (response.data && response.data.success !== false)) {
        setItemsState(prev => ({
          ...prev,
          [item.item_id]: { status: newIsActive ? 'active' : 'inactive' }
        }));
      } else {
        alert('Failed to update status: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      alert('Error updating status: ' + err.message);
    } finally {
      setTogglingItem(null);
    }
  };

  const priceRange = (variants) => {
    if (!variants || !variants.length) return '—';
    const prices = variants.map(v => parseFloat(v.selling_price)).filter(p => !isNaN(p));
    if (!prices.length) return '—';
    const mn = Math.min(...prices), mx = Math.max(...prices);
    return mn === mx ? `₹${mn}` : `₹${mn} – ₹${mx}`;
  };

  return (
    <div className="tab-menu">
      {confirmToggleModal.show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setConfirmToggleModal({ show: false, item: null })}
        >
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>Confirm Action</h3>
              <button
                onClick={() => setConfirmToggleModal({ show: false, item: null })}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>
            <div>
              <p style={{ margin: '10px 0 20px 0', fontSize: '15px', color: '#4b5563' }}>
                Are you sure you want to <strong>{(getItemStatus(confirmToggleModal.item) === 'active') ? 'deactivate' : 'activate'}</strong> this item? This is a soft delete. Deactivating the item will hide it from the consumer app, but it will remain in the system and can be reactivated.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  onClick={() => setConfirmToggleModal({ show: false, item: null })}
                  style={{ flex: 1, padding: '10px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirmToggleStatus}
                  style={{ flex: 1, padding: '10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Yes, Confirm
                </button>

              </div>
            </div>
          </div>
        </div>
      )}
      {/* Menu Filter Controls + Add Item — single row */}
      <div className="menu-filter-controls">
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {businessType === 'R02' ? 'Menu Items' : 'Products'}
        </h3>
        <div className="filter-buttons-left">
          {/* Status Filter */}
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Category Filter */}
          <select
            className="filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          {/* Search Field */}
          <input
            type="text"
            className="search-input"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Refresh Button */}
          <button
            className="btn btn-secondary"
            onClick={() => {
              const handleRefresh = onRefresh || onAddItem;
              if (handleRefresh) handleRefresh();
            }}
            disabled={isLoading}
            title="Refresh menu items"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
          >
            <FiRefreshCw className={isLoading ? 'spin' : ''} size={14} /> Refresh
          </button>

          {/* Add Item — only for R02 restaurants */}
          {businessType === 'R02' && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            >
              <FiPlus size={14} /> Add Item
            </button>
          )}

          {/* Add Product — for R01 (Grocery) and R08 (Fashion/Other) */}
          {(businessType === 'R01' || businessType === 'R08') && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAddProductModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            >
              <FiPlus size={14} /> Add Product
            </button>
          )}
        </div>
      </div>

      <div className="menu-items-content">
        {/* Desktop Table View */}
        {!isMobile && (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Item Name</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  {businessType === 'R02' && <th>Item Type</th>}
                  <th>Original Cost</th>
                  <th>Selling Price</th>
                  <th style={{ textAlign: 'center' }}>Variants<br /><span style={{ fontSize: '11px', fontWeight: 400, color: '#6b7280' }}>Total / Active / Inactive</span></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="loading-container" style={{ padding: '40px 0' }}>
                        <p>Loading menu...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <NoDataDisplay colSpan={9} message="No menu items found" />
                ) : (
                  currentItems.map(item => {
                    const variants = item.variants || [];
                    const totalVariants = variants.length;
                    const activeVariants = variants.filter(v => v.availability_status === 'AVAILABLE' || v.is_active === true || v.status === 'active').length;
                    const inactiveVariants = totalVariants - activeVariants;

                    return (
                      <tr key={item.item_id}>
                        <td>
                          <div className="item-image-cell">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="item-thumbnail"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="no-image-placeholder" style={{ display: item.image ? 'none' : 'flex' }}>
                              No Image
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="item-name">{cleanItemName(item.name)}</span>
                        </td>
                        <td>{item.category || '—'}</td>
                        <td>{item.sub_category || item.subcategory || item.sub_category_name || '—'}</td>
                        {businessType === 'R02' && (
                          <td>
                            <span className="food-type-badge">
                              <span className="type-text">{item.type || '—'}</span>
                            </span>
                          </td>
                        )}
                        <td>
                          {item.original_cost != null && item.original_cost !== '' ? (
                            <span className="price-value">₹{item.original_cost}</span>
                          ) : (
                            <span className="na-value">—</span>
                          )}
                        </td>
                        <td>
                          <span className="price-value">₹{item.price}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {totalVariants > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>{totalVariants}</span>
                              <div style={{ display: 'flex', gap: '6px', fontSize: '11px' }}>
                                <span style={{ color: '#10b981', fontWeight: 600 }}>{activeVariants} active</span>
                                <span style={{ color: '#94a3b8' }}>·</span>
                                <span style={{ color: '#ef4444', fontWeight: 600 }}>{inactiveVariants} inactive</span>
                              </div>
                            </div>
                          ) : (
                            <span className="na-value">—</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {businessType === 'R02' && (
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => setEditingMenuId(item.item_id)}
                                title="Edit Item"
                                style={{ minWidth: '90px' }}
                              >
                                <FiEdit2 /> Edit
                              </button>
                            )}
                            {(businessType === 'R01' || businessType === 'R08') && (
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => setEditingProductId(item.item_id)}
                                title="Edit Product"
                                style={{ minWidth: '90px' }}
                              >
                                <FiEdit2 /> Edit
                              </button>
                            )}
                            <button
                              className={`btn btn-sm ${getItemStatus(item) === 'active' ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleToggleStatus(item)}
                              disabled={togglingItem === item.item_id}
                              title={getItemStatus(item) === 'active' ? 'Deactivate Item' : 'Activate Item'}
                              style={{ minWidth: '90px' }}
                            >
                              {togglingItem === item.item_id ? '...' : (
                                getItemStatus(item) === 'active'
                                  ? <><FiEyeOff /> Deactivate</>
                                  : <><FiEye /> Activate</>
                              )}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => onDeleteItem(item)}
                              title="Delete Item"
                            >
                              <FiTrash2 /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}


        {/* Mobile Card View */}
        {isMobile && (
          <div className="mobile-cards-container">
            {isLoading ? (
              <div className="loading-container" style={{ padding: '40px 0' }}>
                <p>Loading menu...</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <NoDataDisplay message="No menu items found" />
            ) : (
              currentItems.map(item => (
                <div key={item.item_id} className="mobile-item-card">
                  <div className="mobile-card-row">
                    <div className="mobile-card-image-small">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="mobile-no-image-small" style={{ display: item.image ? 'none' : 'flex' }}>
                        No Image
                      </div>
                    </div>
                    <div className="mobile-card-info">
                      <div className="mobile-card-header">
                        <h4 className="mobile-item-name">{cleanItemName(item.name)}</h4>
                        <span className={`mobile-status-badge ${item.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                          {item.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="mobile-card-details">
                        <p className="mobile-category">
                          <strong>Category:</strong> {item.category || 'N/A'}
                        </p>
                        <p className="mobile-price">
                          <strong>Price:</strong> ₹{item.price}
                        </p>
                      </div>
                      <div className="mobile-card-actions" style={{ textAlign: 'right' }}>
                        <button
                          className="mobile-action-btn mobile-delete-btn"
                          onClick={() => onDeleteItem(item)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pagination */}
        <PaginationBar currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
      </div>
      {businessType === 'R02' && showAddModal && (
        <AddNewMenuItemModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddItemSubmit}
          businessId={businessId}
          businessData={businessData}
          isSaving={isAddingItem}
        />
      )}

      {businessType === 'R02' && editingMenuId && (
        <EditMenuItemModal
          itemId={editingMenuId}
          businessId={businessId}
          businessData={businessData}
          onClose={() => setEditingMenuId(null)}
          onSave={handleUpdateItemSubmit}
          onRefresh={onAddItem}
          isSaving={isUpdatingItem}
        />
      )}

      {(businessType === 'R01' || businessType === 'R08') && showAddProductModal && (
        <AddProductModal
          onClose={() => setShowAddProductModal(false)}
          onSave={onAddItem}
          businessId={businessId}
        />
      )}

      {(businessType === 'R01' || businessType === 'R08') && editingProductId && (
        <EditProductModal
          itemId={editingProductId}
          businessId={businessId}
          onClose={() => setEditingProductId(null)}
          onSave={onAddItem}
        />
      )}
    </div>
  );
};

// Orders Tab Component
// Order Details Modal style helpers
const cardStyle = {
  background: 'white', border: '1px solid #f3f4f6',
  borderRadius: '12px', padding: '16px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
};
const cardTitleStyle = {
  display: 'flex', alignItems: 'center', gap: '8px',
  fontWeight: '700', fontSize: '15px', color: '#1f2937',
  marginBottom: '14px'
};
const iconCircleStyle = {
  width: '28px', height: '28px', borderRadius: '50%',
  background: '#fff7ed', color: '#F55D00',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '14px', flexShrink: 0
};
const rowStyle = {
  display: 'grid', gridTemplateColumns: '130px 1fr',
  alignItems: 'flex-start', marginBottom: '10px', gap: '8px'
};
const labelStyle = { color: '#9ca3af', fontSize: '13px', textAlign: 'left' };
const valueStyle = { fontWeight: '500', fontSize: '13px', color: '#1f2937', textAlign: 'left' };
const badgeStyle = {
  padding: '3px 10px', borderRadius: '4px',
  fontSize: '11px', fontWeight: '700', letterSpacing: '0.3px',
  display: 'inline-block', width: 'max-content'
};
const getStatusBg = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'delivered': case 'completed': return '#dcfce7';
    case 'assigned': case 'confirmed': return '#fff7ed';
    case 'pending': return '#fef9c3';
    case 'cancelled': case 'rejected': return '#fee2e2';
    case 'paid': return '#dbeafe';
    default: return '#f3f4f6';
  }
};
const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'delivered': case 'completed': return '#16a34a';
    case 'assigned': case 'confirmed': return '#F55D00';
    case 'pending': return '#ca8a04';
    case 'cancelled': case 'rejected': return '#dc2626';
    case 'paid': return '#1e40af';
    default: return '#6b7280';
  }
};

const getDefaultDateFrom = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const getDefaultDateTo = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OrdersTab = ({ businessId, onRefresh, isMobile, businessType }) => {
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom);
  const [dateTo, setDateTo] = useState(getDefaultDateTo);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [fullOrderDetails, setFullOrderDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showGstBreakdown, setShowGstBreakdown] = useState(false);
  const PAGE_SIZE = 20;
  const [exportLoading, setExportLoading] = useState(false);
  const [serverPagination, setServerPagination] = useState({
    total_orders: 0,
    page: 1,
    limit: 20,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  // Fetch orders from the dedicated all-orders API with server-side filters
  const fetchOrders = useCallback(async (page = 1) => {
    try {
      setOrdersLoading(true);
      const response = await AdminService.getBusinessAllOrders(businessId, {
        page: page,
        limit: PAGE_SIZE,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        order_type: orderTypeFilter !== 'all' ? orderTypeFilter : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: searchTerm || undefined
      });
      if (response.success) {
        setOrders(response.orders || []);
        setServerPagination(response.pagination || {
          total_orders: 0, page: page, limit: PAGE_SIZE,
          total_pages: 1, has_next: false, has_prev: false
        });
      } else {
        console.error('Failed to fetch orders:', response.message);
        setOrders([]);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [businessId, statusFilter, orderTypeFilter, dateFrom, dateTo, searchTerm]);

  useEffect(() => {
    if (businessId) {
      fetchOrders(currentPage);
    }
  }, [businessId, currentPage, fetchOrders]);

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      // Apply all active filters to the full orders list (not just current page)
      const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
      const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;

      const allFiltered = orders.filter(order => {
        // Date range
        if (from || to) {
          const d = new Date(order.created_at || order.date);
          if (from && d < from) return false;
          if (to && d > to) return false;
        }
        // Search
        if (searchTerm && !order.order_number.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        // Status
        if (statusFilter !== 'all' && (order.status || '').toLowerCase() !== statusFilter.toLowerCase()) return false;
        // Order type
        if (orderTypeFilter !== 'all') {
          const t = order.order_type || 'pick_up';
          if (t.toLowerCase() !== orderTypeFilter.toLowerCase()) return false;
        }
        return true;
      });

      if (allFiltered.length === 0) return;

      const rows = allFiltered.map(order => ({
        'Order ID': `#${order.order_id || order.order_number}`,
        'Customer': order.customer_name || 'N/A',
        'Subtotal (?)': order.total_amount ?? order.subtotal ?? order.final_amount ?? order.amount ?? 0,
        'Delivery Charges (?)': order.delivery_charges ?? 0,
        'Final Amount (?)': order.final_amount ?? order.amount ?? 0,
        'Order Type': order.order_type ? (order.order_type.charAt(0).toUpperCase() + order.order_type.slice(1).toLowerCase()) : 'N/A',
        'Status': order.status || 'N/A',
        'Delivery Partner': order.delivery_partner || 'Unassigned',
        'Date': new Date(order.created_at || order.date).toLocaleDateString('en-GB'),
        'Time': order.created_at ? new Date(order.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : (order.order_time ? order.order_time.substring(0, 5) : '-'),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

      // Auto-width columns
      worksheet['!cols'] = Object.keys(rows[0]).map(key => ({
        wch: Math.max(key.length, ...rows.map(r => String(r[key] ?? '').length)) + 2
      }));

      // Filename with active filters
      const parts = ['orders'];
      if (dateFrom) parts.push(`from-${dateFrom}`);
      if (dateTo) parts.push(`to-${dateTo}`);
      if (statusFilter !== 'all') parts.push(statusFilter);
      if (orderTypeFilter !== 'all') parts.push(orderTypeFilter);
      XLSX.writeFile(workbook, `${parts.join('_')}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewOrderDetails = async (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsModal(true);
    setFullOrderDetails(null);
    setOrderDetailsLoading(true);
    setShowGstBreakdown(false);
    try {
      let url = `${API_ENDPOINTS.BASE_URL}/api/v1/admin/orders/${order.order_id}/?order_system=standard`;
      if (order.is_counter_order) {
        url = `${API_ENDPOINTS.BASE_URL}/api/v1/admin/orders/counter/${order.order_id}/`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.success && data.data) {
        if (data.data.order) {
          setFullOrderDetails(data.data.order);
        } else {
          setFullOrderDetails(data.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const handleCloseOrderDetailsModal = () => {
    setShowOrderDetailsModal(false);
    setSelectedOrder(null);
    setFullOrderDetails(null);
  };

  // Get unique statuses and order types for filter options
  const getUniqueStatuses = () => {
    return [
      'pending', 'confirmed', 'preparing', 'ready', 'assigned', 'out_for_delivery', 'delivered',
      'completed', 'cancelled', 'rejected', 'paid',
      'searching_rider', 'rider_assigned', 'rider_accepted', 'on_the_way_to_pickup',
      'at_pickup', 'picked_up', 'in_transit', 'at_delivery'
    ];
  };

  const getUniqueOrderTypes = () => {
    return ['delivery', 'takeaway', 'pick_up', 'dine_in', 'menu'];
  };

  // Filter orders based on all criteria (handled server-side now)
  const getFilteredOrders = () => {
    return orders;
  };

  const filteredOrders = getFilteredOrders();
  const uniqueStatuses = getUniqueStatuses();
  const uniqueOrderTypes = getUniqueOrderTypes();

  // Server-side pagination - use API pagination data
  const totalPages = serverPagination.total_pages || 1;

  // Reset to page 1 whenever filters change (server-side filters apply on page 1 of new subset)
  const handleSearchChange = (val) => { setSearchTerm(val); setCurrentPage(1); };
  const handleStatusFilterChange = (val) => { setStatusFilter(val); setCurrentPage(1); };
  const handleOrderTypeFilterChange = (val) => { setOrderTypeFilter(val); setCurrentPage(1); };
  const handleDateFromChange = (val) => { setDateFrom(val); setCurrentPage(1); };
  const handleDateToChange = (val) => { setDateTo(val); setCurrentPage(1); };

  if (ordersLoading && orders.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTop: '3px solid #F55D00', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }}></div>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-orders">
      <div className="orders-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: 0 }}>Orders ({filteredOrders.length} of {serverPagination.total_orders || orders.length})</h3>
        <button
          onClick={handleExportExcel}
          disabled={exportLoading || filteredOrders.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px',
            background: exportLoading || filteredOrders.length === 0 ? '#9ca3af' : '#1d6f42',
            color: 'white', border: 'none', borderRadius: '6px',
            fontSize: '13px', fontWeight: '600',
            cursor: exportLoading || filteredOrders.length === 0 ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={e => { if (!exportLoading && filteredOrders.length > 0) e.currentTarget.style.background = '#155534'; }}
          onMouseLeave={e => { if (!exportLoading && filteredOrders.length > 0) e.currentTarget.style.background = '#1d6f42'; }}
        >
          {exportLoading
            ? <><MdOutlineChevronRight style={{ animation: 'spin 0.8s linear infinite' }} /> {isMobile ? '' : 'Exporting...'}</>
            : <><FiDownload size={14} /> {isMobile ? '' : 'Excel'}</>
          }
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="orders-filter-controls">
        <div className="filter-left">
          {/* Search by Order ID */}
          <input
            type="text"
            className="search-input"
            placeholder="Search by Order ID..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />

          {/* Status Filter */}
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
          >
            <option value="all">All Status</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>
                {status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </option>
            ))}
          </select>

          {/* Order Type Filter */}
          <select
            className="filter-select"
            value={orderTypeFilter}
            onChange={(e) => handleOrderTypeFilterChange(e.target.value)}
          >
            <option value="all">All Types</option>
            {uniqueOrderTypes.map(type => (
              <option key={type} value={type}>
                {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-right">
          <div style={{ display: 'flex', alignItems: 'end', gap: '8px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>From:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                style={{
                  padding: '7px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#374151',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>To:</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => handleDateToChange(e.target.value)}
                style={{
                  padding: '7px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#374151',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
            </div>
            <button
              onClick={() => {
                setDateFrom(getDefaultDateFrom());
                setDateTo(getDefaultDateTo());
                setCurrentPage(1);
              }}
              style={{
                padding: '7px 12px', border: '1px solid #e5e7eb',
                borderRadius: '6px', background: 'white', color: '#6b7280',
                fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap'
              }}
            >
              {isMobile ? '↺' : 'Reset'}
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Subtotal</th>
              <th>Delivery Charges</th>
              <th>Final Amount</th>
              <th>Order Type</th>
              <th>Order Status</th>
              <th>Delivery Partner</th>
              <th>Date & Time</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <NoDataDisplay colSpan={10} message="No orders found" />
            ) : (
              filteredOrders.map(order => (
                <tr key={order.order_id}>
                  <td><span className="order-id" title={`#${order.order_id}`}>#{order.order_id}</span></td>
                  <td><span className="customer-name">{order.customer_name || 'N/A'}</span></td>
                  <td><span className="subtotal-amount">₹{(order.total_amount ?? order.subtotal ?? order.final_amount ?? order.amount ?? 0).toLocaleString()}</span></td>
                  <td>
                    <span className="delivery-fee">
                      ₹{(order.delivery_charges ?? 0).toLocaleString()}
                    </span>
                  </td>
                  <td><span className="final-amount">₹{(order.final_amount ?? order.amount ?? 0).toLocaleString()}</span></td>
                  <td>
                    <span className="order-type-text">
                      {order.order_type ? (order.order_type.charAt(0).toUpperCase() + order.order_type.slice(1).toLowerCase()) : 'Pickup'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${order.status?.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <span className={`delivery-partner ${order.delivery_partner === 'Unassigned' ? 'unassigned' : 'assigned'}`}>
                      {order.delivery_partner || 'Unassigned'}
                    </span>
                  </td>
                  <td>
                    <span className="order-date-time">
                      {new Date(order.created_at || order.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      <br />
                      <span className="order-time">{order.created_at ? new Date(order.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : (order.order_time ? order.order_time.substring(0, 5) : '-')}</span>
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleViewOrderDetails(order)}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e04d00';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#F55D00';
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#F55D00',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(245, 93, 0, 0.2)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FiEye style={{ fontSize: '14px' }} />
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>



      {/* Mobile-only cards - hidden on desktop */}
      <div className="orders-mobile-cards" style={{ display: 'none' }}>
        {filteredOrders.length === 0 ? (
          <NoDataDisplay message="No orders found" />
        ) : (
          filteredOrders.map(order => (
            <div key={order.order_id} className="order-card-mobile">
              <div className="order-card-header">
                <span className="order-id-mobile">#{order.order_id}</span>
                <span className={`order-status-badge badge badge-${order.status}`}>
                  {order.status}
                </span>
              </div>
              <div className="order-card-details">
                <div className="order-detail-item">
                  <span className="order-detail-label">Customer</span>
                  <span className="order-detail-value">{order.customer_name || 'N/A'}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Amount</span>
                  <span className="order-detail-value">₹{(order.final_amount ?? order.amount ?? 0).toLocaleString()}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Order Type</span>
                  <span className="order-detail-value">{order.order_type ? (order.order_type.charAt(0).toUpperCase() + order.order_type.slice(1).toLowerCase()) : 'Pickup'}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Date</span>
                  <span className="order-detail-value">{new Date(order.created_at || order.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Time</span>
                  <span className="order-detail-value">{order.created_at ? new Date(order.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : (order.order_time ? order.order_time.substring(0, 5) : '-')}</span>
                </div>
                <div className="order-detail-item">
                  <span className="order-detail-label">Delivery Partner</span>
                  <span className="order-detail-value">{order.delivery_partner || 'Unassigned'}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '16px', padding: '12px 4px'
        }}>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, serverPagination.total_orders)} of {serverPagination.total_orders} orders {ordersLoading ? '(loading...)' : ''}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                width: '32px', height: '32px', borderRadius: '6px',
                border: '1px solid #e5e7eb', background: 'white',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                color: currentPage === 1 ? '#d1d5db' : '#374151',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <MdOutlineChevronLeft size={18} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: '13px' }}>�</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    style={{
                      width: '32px', height: '32px', borderRadius: '6px',
                      border: '1px solid ' + (currentPage === item ? '#F55D00' : '#e5e7eb'),
                      background: currentPage === item ? '#F55D00' : 'white',
                      color: currentPage === item ? 'white' : '#374151',
                      cursor: 'pointer', fontSize: '13px', fontWeight: currentPage === item ? '600' : '400'
                    }}
                  >
                    {item}
                  </button>
                )
              )
            }

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                width: '32px', height: '32px', borderRadius: '6px',
                border: '1px solid #e5e7eb', background: 'white',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                color: currentPage === totalPages ? '#d1d5db' : '#374151',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <MdOutlineChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
      {/* Order Details Modal */}
      {showOrderDetailsModal && selectedOrder && (
        <div
          onClick={handleCloseOrderDetailsModal}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 99999, padding: '20px'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#f9fafb', borderRadius: '16px',
              width: '100%', maxWidth: '820px',
              maxHeight: '92vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
            }}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #F55D00 0%, #ff8c42 100%)',
              padding: '20px 24px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              borderRadius: '16px 16px 0 0', flexShrink: 0
            }}>
              <div>
                <h2 style={{ margin: 0, color: 'white', fontSize: '22px', fontWeight: '700' }}>
                  Order #{fullOrderDetails?.token_num || selectedOrder.order_number}
                </h2>
                <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                  {fullOrderDetails?.order_number || selectedOrder.order_id}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  background: 'rgba(255,255,255,0.2)', color: 'white',
                  padding: '5px 14px', borderRadius: '20px',
                  fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px',
                  border: '1px solid rgba(255,255,255,0.4)', textTransform: 'uppercase'
                }}>
                  {fullOrderDetails?.order_type || selectedOrder.db_order_type || selectedOrder.order_type || 'N/A'}
                </span>
                <button
                  onClick={handleCloseOrderDetailsModal}
                  style={{
                    background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
                    color: 'white', width: '32px', height: '32px', borderRadius: '50%',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
              {orderDetailsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <div style={{
                    width: '36px', height: '36px', border: '3px solid #f3f4f6',
                    borderTop: '3px solid #F55D00', borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite', margin: '0 auto 12px'
                  }} />
                  Loading order details...
                </div>
              ) : (
                <>
                  {/* Status Timeline Card */}
                  {Array.isArray(fullOrderDetails?.status_timeline) && fullOrderDetails.status_timeline.length > 0 && (
                    <div style={{ ...cardStyle, marginBottom: '16px' }}>
                      <div style={cardTitleStyle}>
                        <span style={iconCircleStyle}><FiClock size={14} /></span>
                        Status Timeline
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {fullOrderDetails.status_timeline.map((st, i) => (
                          <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < fullOrderDetails.status_timeline.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                            <div style={{ width: '12px', display: 'flex', justifyContent: 'center' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F55D00', marginTop: '6px' }} />
                            </div>
                            <div style={{ flex: 1, textAlign: 'left' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{st.label || st.status}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                  {st.timestamp ? new Date(st.timestamp).toLocaleString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  }) : ''}
                                </div>
                              </div>
                              {st.notes && <div style={{ marginTop: '6px', color: '#334155', fontSize: '13px' }}>{st.notes}</div>}
                              <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>
                                By: {st.changed_by || st.source || 'system'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Row 1: Order Info + Customer Details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div style={cardStyle}>
                      <div style={cardTitleStyle}>
                        <span style={iconCircleStyle}><FiInfo size={14} /></span>
                        Order Information
                      </div>
                      <div style={rowStyle}>
                        <span style={labelStyle}>Order Status:</span>
                        <span style={{
                          ...badgeStyle,
                          background: getStatusBg(fullOrderDetails?.status || selectedOrder.status),
                          color: getStatusColor(fullOrderDetails?.status || selectedOrder.status),
                          border: `1px solid ${getStatusColor(fullOrderDetails?.status || selectedOrder.status)}`
                        }}>
                          {(fullOrderDetails?.status || selectedOrder.status || 'N/A').toUpperCase()}
                        </span>
                      </div>
                      <div style={rowStyle}>
                        <span style={labelStyle}>Payment Status:</span>
                        <span style={{
                          ...badgeStyle,
                          background: '#dcfce7', color: '#16a34a', border: '1px solid #16a34a'
                        }}>
                          {(fullOrderDetails?.payment_status || selectedOrder.payment_status || 'N/A').toUpperCase()}
                        </span>
                      </div>
                      <div style={rowStyle}>
                        <span style={labelStyle}>Created Date:</span>
                        <span style={valueStyle}>
                          {new Date(fullOrderDetails?.created_at || selectedOrder.created_at || selectedOrder.date).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>

                    <div style={cardStyle}>
                      <div style={cardTitleStyle}>
                        <span style={iconCircleStyle}><FiUser size={14} /></span>
                        Customer Details
                      </div>
                      <div style={rowStyle}>
                        <span style={labelStyle}>Customer Name:</span>
                        <span style={valueStyle}>
                          {fullOrderDetails?.customer?.display_name || fullOrderDetails?.customer_details?.first_name || selectedOrder.customer_name || 'N/A'}
                        </span>
                      </div>
                      <div style={rowStyle}>
                        <span style={labelStyle}>Phone Number:</span>
                        <span style={valueStyle}>{fullOrderDetails?.customer?.phone || fullOrderDetails?.customer_details?.phone_number || 'N/A'}</span>
                      </div>
                      {(fullOrderDetails?.order_type || selectedOrder.order_type || '').toLowerCase() === 'delivery' && (
                        <div style={rowStyle}>
                          <span style={labelStyle}>Delivery Address:</span>
                          <span style={{ ...valueStyle, whiteSpace: 'normal', lineHeight: '1.4' }}>
                            {(() => {
                              if (fullOrderDetails?.delivery_address_details?.raw_address) {
                                try {
                                  const rawAddress = JSON.parse(fullOrderDetails.delivery_address_details.raw_address);
                                  const addressParts = [
                                    rawAddress.door_no,
                                    rawAddress.street,
                                    rawAddress.city,
                                    rawAddress.state,
                                    rawAddress.pincode,
                                    rawAddress.country
                                  ].filter(part => part);
                                  return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
                                } catch (e) {
                                  return 'N/A';
                                }
                              }
                              return (typeof fullOrderDetails?.delivery_address === 'string' ? fullOrderDetails.delivery_address : null) ||
                                fullOrderDetails?.delivery_address_details?.formatted_address ||
                                fullOrderDetails?.customer?.address ||
                                selectedOrder.delivery_address ||
                                'N/A';
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Delivery Partner (delivery only) + Business Details */}
                  {(() => {
                    const orderType = (fullOrderDetails?.order_type || selectedOrder.order_type || '').toLowerCase();
                    const isDelivery = orderType === 'delivery';
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: isDelivery ? '1fr 1fr' : '1fr', gap: '16px', marginBottom: '16px' }}>
                        {isDelivery && (
                          <div style={cardStyle}>
                            <div style={cardTitleStyle}>
                              <span style={iconCircleStyle}><MdOutlineTwoWheeler size={15} /></span>
                              Delivery Partner Details
                            </div>
                            {(fullOrderDetails?.delivery_partner || fullOrderDetails?.delivery_partner_details) ? (
                              <>
                                <div style={rowStyle}>
                                  <span style={labelStyle}>Name:</span>
                                  <span style={valueStyle}>{fullOrderDetails.delivery_partner?.name || fullOrderDetails.delivery_partner_details?.delivery_partner_name || 'N/A'}</span>
                                </div>
                                <div style={rowStyle}>
                                  <span style={labelStyle}>Vehicle Type:</span>
                                  <span style={valueStyle}>{fullOrderDetails.delivery_partner?.vehicle_type || 'N/A'}</span>
                                </div>
                                <div style={rowStyle}>
                                  <span style={labelStyle}>Phone:</span>
                                  <span style={valueStyle}>{fullOrderDetails.delivery_partner?.phone || fullOrderDetails.delivery_partner_details?.delivery_partner_phone || 'N/A'}</span>
                                </div>
                              </>
                            ) : (
                              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>No delivery partner assigned</p>
                            )}
                          </div>
                        )}

                        <div style={cardStyle}>
                          <div style={cardTitleStyle}>
                            <span style={iconCircleStyle}><MdOutlineStorefront size={15} /></span>
                            Business Details
                          </div>
                          <div style={rowStyle}>
                            <span style={labelStyle}>Business Name:</span>
                            <span style={valueStyle}>{fullOrderDetails?.business_details?.business_name || 'N/A'}</span>
                          </div>
                          <div style={rowStyle}>
                            <span style={labelStyle}>Contact Number:</span>
                            <span style={valueStyle}>
                              {fullOrderDetails?.business_contact?.contact_mobile ||
                                fullOrderDetails?.business_contact?.business_number ||
                                fullOrderDetails?.business_details?.business_number || 'N/A'}
                            </span>
                          </div>
                          {fullOrderDetails?.business_details?.address && (
                            <div style={{ marginTop: '8px' }}>
                              <div style={rowStyle}>
                                <span style={labelStyle}>Business Address:</span>
                                <span style={{ ...valueStyle, maxWidth: '100%', lineHeight: '1.5' }}>
                                  {fullOrderDetails.business_details.address}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Delivery Address - only when available */}
                  {fullOrderDetails?.delivery_address_details?.formatted_address && (
                    <div style={{ ...cardStyle, marginBottom: '16px' }}>
                      <div style={cardTitleStyle}>
                        <span style={iconCircleStyle}><FiMapPin size={14} /></span>
                        Delivery Address
                      </div>
                      <div style={rowStyle}>
                        <span style={labelStyle}>Address:</span>
                        <span style={{ ...valueStyle, maxWidth: '100%' }}>
                          {fullOrderDetails.delivery_address_details.formatted_address}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Row 3: Order Items + Order Summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={cardStyle}>
                      <div style={cardTitleStyle}>
                        <span style={iconCircleStyle}><FiPackage size={14} /></span>
                        Order Items ({(fullOrderDetails?.items || []).length})
                      </div>
                      {(fullOrderDetails?.items || []).length === 0 ? (
                        <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>No item details available</p>
                      ) : (
                        (fullOrderDetails.items).map((item, idx) => (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '10px 0',
                            borderBottom: idx < fullOrderDetails.items.length - 1 ? '1px solid #f3f4f6' : 'none'
                          }}>
                            {item.image || item.item_image ? (
                              <img src={item.image || item.item_image} alt={item.name || item.item_name}
                                style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                            ) : (
                              <div style={{
                                width: '48px', height: '48px', borderRadius: '8px',
                                background: '#f3f4f6', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', flexShrink: 0, color: '#9ca3af'
                              }}>
                                <FiShoppingCart size={20} />
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.name || item.item_name || item.product_name || item.menu_item?.name || item.item?.name || 'Unknown Item'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#9ca3af' }}>{item.category || item.size_label || 'Unknown'}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                Qty: {item.quantity}&nbsp;&nbsp;Unit: &#8377;{(Number(item.unit_price || item.price || 0)).toLocaleString()}
                              </div>
                            </div>
                            <div style={{ fontWeight: '700', color: '#F55D00', fontSize: '15px', flexShrink: 0 }}>
                              &#8377;{(Number(item.total_price || item.line_total || item.sub_total || ((item.unit_price || item.price || 0) * (item.quantity || 1)))).toLocaleString()}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div style={cardStyle}>
                      <div style={cardTitleStyle}>
                        <span style={iconCircleStyle}><MdOutlineReceiptLong size={15} /></span>
                        Order Summary
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Subtotal:</span>
                          <span style={valueStyle}>
                            &#8377;{Number((fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.subtotal ?? selectedOrder.subtotal ?? selectedOrder.amount ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Delivery Charges:</span>
                          <span style={valueStyle}>
                            &#8377;{Number((fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.delivery_charges ?? selectedOrder.delivery_charges ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        {((fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.parcel_charges > 0) && (
                          <div style={rowStyle}>
                            <span style={labelStyle}>Parcel Charges:</span>
                            <span style={valueStyle}>&#8377;{Number((fullOrderDetails?.summary || fullOrderDetails?.order_summary).parcel_charges).toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        <div style={{ ...rowStyle, cursor: 'pointer' }} onClick={() => setShowGstBreakdown(!showGstBreakdown)}>
                          <span style={labelStyle}>
                            {(businessType || fullOrderDetails?.business_type || fullOrderDetails?.business_details?.business_type || selectedOrder?.business_type || '') === 'R02' ? 'GST (5%):' : 'Tax:'}
                            <span style={{ marginLeft: '4px', verticalAlign: 'middle', display: 'inline-block' }}>
                              {showGstBreakdown ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                            </span>
                          </span>
                          <span style={valueStyle}>&#8377;{Number((fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.tax ?? (fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.gst_total ?? 0).toLocaleString('en-IN')}</span>
                        </div>
                        {showGstBreakdown && (
                          <div style={{ padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {(() => {
                              const gstGroups = {};
                              (fullOrderDetails?.items || []).forEach(item => {
                                const taxAmount = Number(item.tax_amount || item.gst || 0);
                                if (taxAmount > 0) {
                                  const unitPrice = Number(item.unit_price || item.price || 0);
                                  const qty = Number(item.quantity || 1);
                                  const totalPrice = unitPrice * qty;

                                  let percentage = 5;
                                  if (item.tax_percent) percentage = Number(item.tax_percent);
                                  else if (item.gst_percentage) percentage = Number(item.gst_percentage);
                                  else if (totalPrice > 0) percentage = Math.round((taxAmount / totalPrice) * 100);

                                  const halfPercent = percentage / 2;
                                  if (!gstGroups[halfPercent]) gstGroups[halfPercent] = 0;
                                  gstGroups[halfPercent] += taxAmount / 2;
                                }
                              });

                              if (Object.keys(gstGroups).length === 0) return null;

                              return Object.entries(gstGroups).map(([halfPercent, amount], idx) => (
                                <React.Fragment key={idx}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>CGST ({halfPercent}%)</span>
                                    <span>₹{Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>SGST ({halfPercent}%)</span>
                                    <span>₹{Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                </React.Fragment>
                              ));
                            })()}
                          </div>
                        )}
                        <div style={{
                          borderTop: '2px solid #f3f4f6', paddingTop: '12px', marginTop: '4px',
                          display: 'grid', gridTemplateColumns: '130px 1fr', gap: '8px', alignItems: 'center'
                        }}>
                          <span style={{ fontWeight: '700', fontSize: '16px', color: '#1f2937' }}>Total Amount:</span>
                          <span style={{ fontWeight: '800', fontSize: '20px', color: '#F55D00', textAlign: 'left' }}>
                            &#8377;{Number((fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.total ?? (fullOrderDetails?.summary || fullOrderDetails?.order_summary)?.total_amount ?? selectedOrder.amount ?? 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #f3f4f6',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0, background: 'white', borderRadius: '0 0 16px 16px'
            }}>

              <button
                onClick={handleCloseOrderDetailsModal}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: '8px', background: '#F55D00', color: 'white',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Performance Tab Component - Professional Clean Design
const PerformanceTab = ({ business, performance }) => {
  return (
    <div className="tab-performance-new">
      {/* Top Row - Key Metrics (4 cards) */}
      <div className="performance-metrics-row">
        <div className="perf-metric-card">
          <div className="perf-icon-wrapper primary">
            <FiShoppingCart className="perf-icon" />
          </div>
          <div className="perf-content">
            <div className="perf-value">{performance.total_orders || 0}</div>
            <div className="perf-label">Total Orders</div>
          </div>
        </div>

        <div className="perf-metric-card">
          <div className="perf-icon-wrapper success">
            <FiDollarSign className="perf-icon" />
          </div>
          <div className="perf-content">
            <div className="perf-value">₹{(performance.total_revenue || 0).toLocaleString()}</div>
            <div className="perf-label">Total Revenue</div>
          </div>
        </div>

        <div className="perf-metric-card">
          <div className="perf-icon-wrapper warning">
            <FiStar className="perf-icon" />
          </div>
          <div className="perf-content">
            <div className="perf-value">{(performance.avg_rating || 0).toFixed(1)}</div>
            <div className="perf-label">Average Rating</div>
          </div>
        </div>

        <div className="perf-metric-card">
          <div className="perf-icon-wrapper info">
            <FiBarChart className="perf-icon" />
          </div>
          <div className="perf-content">
            <div className="perf-value">₹{(performance.avg_order_value || 0).toFixed(0)}</div>
            <div className="perf-label">Avg Order Value</div>
          </div>
        </div>
      </div>

      {/* Middle Row - Performance Details (2 columns) */}
      <div className="performance-details-row">
        {/* Order Statistics */}
        <div className="perf-detail-card">
          <div className="perf-card-header">
            <h4>Order Statistics</h4>
          </div>
          <div className="perf-stats-list">
            <div className="perf-stat-item">
              <div className="stat-item-left">
                <FiCheckCircle className="stat-item-icon success" />
                <span className="stat-item-label">Completed Orders</span>
              </div>
              <span className="stat-item-value">{performance.completed_orders || 0}</span>
            </div>
            <div className="perf-stat-item">
              <div className="stat-item-left">
                <FiClock className="stat-item-icon warning" />
                <span className="stat-item-label">Pending Orders</span>
              </div>
              <span className="stat-item-value">{performance.pending_orders || 0}</span>
            </div>
            <div className="perf-stat-item">
              <div className="stat-item-left">
                <FiPackage className="stat-item-icon info" />
                <span className="stat-item-label">Processing Orders</span>
              </div>
              <span className="stat-item-value">{performance.processing_orders || 0}</span>
            </div>
            <div className="perf-stat-item">
              <div className="stat-item-left">
                <FiTruck className="stat-item-icon primary" />
                <span className="stat-item-label">Out for Delivery</span>
              </div>
              <span className="stat-item-value">{performance.delivery_orders || 0}</span>
            </div>
          </div>
        </div>

        {/* Business Insights */}
        <div className="perf-detail-card">
          <div className="perf-card-header">
            <h4>Business Insights</h4>
          </div>
          <div className="perf-insights-list">
            <div className="insight-item">
              <div className="insight-icon-wrapper">
                <FiClock className="insight-icon" />
              </div>
              <div className="insight-content">
                <span className="insight-label">Peak Hours</span>
                <span className="insight-value">12:00 PM - 2:00 PM</span>
              </div>
            </div>
            <div className="insight-item">
              <div className="insight-icon-wrapper">
                <FiCalendar className="insight-icon" />
              </div>
              <div className="insight-content">
                <span className="insight-label">Best Day</span>
                <span className="insight-value">Saturday</span>
              </div>
            </div>
            <div className="insight-item">
              <div className="insight-icon-wrapper">
                <FiTrendingUp className="insight-icon" />
              </div>
              <div className="insight-content">
                <span className="insight-label">Growth Rate</span>
                <span className="insight-value success-text">+15%</span>
              </div>
            </div>
            <div className="insight-item">
              <div className="insight-icon-wrapper">
                <FiUsers className="insight-icon" />
              </div>
              <div className="insight-content">
                <span className="insight-label">Customer Satisfaction</span>
                <span className="insight-value">{performance.customer_satisfaction || 90}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row - Performance Rates (Progress Bars) */}
      <div className="performance-rates-card">
        <div className="perf-card-header">
          <h4>Performance Rates</h4>
        </div>
        <div className="perf-rates-grid">
          <div className="rate-item">
            <div className="rate-header">
              <span className="rate-label">Order Completion Rate</span>
              <span className="rate-percentage">{performance.completion_rate || 0}%</span>
            </div>
            <div className="rate-bar-container">
              <div
                className="rate-bar success"
                style={{ width: `${performance.completion_rate || 0}%` }}
              ></div>
            </div>
          </div>

          <div className="rate-item">
            <div className="rate-header">
              <span className="rate-label">On-Time Delivery</span>
              <span className="rate-percentage">{performance.on_time_delivery || 85}%</span>
            </div>
            <div className="rate-bar-container">
              <div
                className="rate-bar primary"
                style={{ width: `${performance.on_time_delivery || 85}%` }}
              ></div>
            </div>
          </div>

          <div className="rate-item">
            <div className="rate-header">
              <span className="rate-label">Customer Retention</span>
              <span className="rate-percentage">{performance.customer_retention || 75}%</span>
            </div>
            <div className="rate-bar-container">
              <div
                className="rate-bar warning"
                style={{ width: `${performance.customer_retention || 75}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payouts Tab Component
const PayoutsTab = ({ businessId }) => (
  <div className="tab-payouts">
    <h3>Payouts</h3>
    <p className="info-message">Payout system coming soon...</p>
  </div>
);

// Reviews Tab Component
const ReviewsTab = ({ businessId }) => {
  const [feedback, setFeedback] = useState([]);
  const [productRatings, setProductRatings] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('business');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadFeedback = async () => {
    if (!businessId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await AdminService.getBusinessFeedback(businessId);
      if (res.success) {
        setFeedback(res.business_ratings || []);
        setProductRatings(res.product_ratings || []);
      } else {
        setFetchError(res.message || 'Failed to load feedback');
      }
    } catch (err) {
      setFetchError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [businessId]);

  const handleDeleteFeedback = (id, type, name) => {
    setDeleteConfirm({ id, type, name: name || 'this user' });
  };

  const executeDeleteFeedback = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      const res = await AdminService.deleteBusinessFeedback(businessId, deleteConfirm.type, deleteConfirm.id);
      if (res.success) {
        loadFeedback();
      } else {
        alert(res.message || 'Failed to delete rating');
      }
    } catch (err) {
      alert('Error deleting rating');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  };

  const activeData = activeSubTab === 'business' ? feedback : productRatings;

  // Overall average across all individual activeData entries
  const averageRating = activeData.length
    ? (activeData.reduce((s, f) => s + f.rating, 0) / activeData.length).toFixed(1)
    : 0;

  const ratingCounts = {};
  for (let i = 5; i >= 1; i--) {
    ratingCounts[i] = activeData.filter(f => f.rating === i).length;
  }

  if (loading) {
    return (
      <div className="tab-reviews">
        <NoDataDisplay message="Loading feedback..." />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="tab-reviews">
        <NoDataDisplay message={fetchError} className="no-data-error" />
      </div>
    );
  }

  return (
    <div className="tab-reviews">
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveSubTab('business')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeSubTab === 'business' ? '600' : '400',
            color: activeSubTab === 'business' ? '#1890ff' : '#666',
            borderBottom: activeSubTab === 'business' ? '2px solid #1890ff' : 'none',
            padding: '4px 8px'
          }}
        >
          Business Ratings ({feedback.length})
        </button>
        <button
          onClick={() => setActiveSubTab('products')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeSubTab === 'products' ? '600' : '400',
            color: activeSubTab === 'products' ? '#1890ff' : '#666',
            borderBottom: activeSubTab === 'products' ? '2px solid #1890ff' : 'none',
            padding: '4px 8px'
          }}
        >
          Product Ratings ({productRatings.length})
        </button>
      </div>

      {activeData.length === 0 ? (
        <NoDataDisplay message={`No ${activeSubTab} reviews yet`} />
      ) : (
        <>
          {/* Reviews Summary Section */}
          <div className="reviews-summary">
            <div className="rating-summary-card">
              <div className="rating-summary-content">
                {/* Average Rating Card */}
                <div className="rating-overview">
                  <div className="average-rating">{averageRating}</div>
                  <div className="rating-stars">
                    {Array.from({ length: 5 }, (_, i) => (
                      <MdOutlineStarOutline
                        key={i}
                        style={{
                          color: i < Math.round(averageRating) ? '#fbbf24' : '#e5e7eb',
                          fontSize: '20px'
                        }}
                      />
                    ))}
                  </div>
                  <div className="total-reviews">{activeData.length} reviews</div>
                </div>

                {/* Rating Breakdown */}
                <div className="rating-breakdown">
                  <h4>Rating Distribution</h4>
                  {Object.entries(ratingCounts).reverse().map(([rating, count]) => (
                    <div key={rating} className="rating-bar">
                      <div className="rating-label">
                        <span>{rating}</span>
                        <MdOutlineStarOutline style={{ color: '#fbbf24', fontSize: '12px' }} />
                      </div>
                      <div className="bar-container">
                        <div
                          className="bar-fill"
                          style={{ width: `${activeData.length > 0 ? (count / activeData.length) * 100 : 0}%` }}
                        />
                      </div>
                      <div className="bar-count">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="reviews-list">
            {activeData.map(item => {
              const id = activeSubTab === 'business' ? item.feedback_id : item.rating_id;
              const name = activeSubTab === 'business' ? item.user_name : item.customer_name;
              const comments = activeSubTab === 'business' ? item.additional_comments : item.review;
              const itemName = activeSubTab === 'products' ? item.item_name : null;

              return (
                <div key={id} className="review-card">
                  <div className="review-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="customer-name">{name}</span>
                      <span className="rating">
                        {Array.from({ length: 5 }, (_, i) => (
                          <MdOutlineStarOutline
                            key={i}
                            style={{
                              color: i < item.rating ? '#fbbf24' : '#e5e7eb',
                              fontSize: '14px'
                            }}
                          />
                        ))}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteFeedback(id, activeSubTab, name)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff4d4f',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px'
                      }}
                      title="Delete Rating"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                    <div>
                      {itemName && <p className="review-item-name" style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#666', fontWeight: '500' }}>Product: {itemName}</p>}
                      <p className="review-text" style={{ margin: 0 }}>{comments || ''}</p>
                    </div>
                    <p className="review-date" style={{ margin: 0, whiteSpace: 'nowrap' }}>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div
          onClick={() => !deleteLoading && setDeleteConfirm(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 999999
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', padding: '32px',
              maxWidth: '420px', width: '90%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center'
            }}
          >
            {/* Icon */}
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#fee2e2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px'
            }}>
              <MdOutlineInfo style={{ color: '#dc2626', fontSize: '24px' }} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
              Delete Rating?
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
              Are you sure you want to delete the rating from <strong>"{deleteConfirm.name}"</strong>?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
                style={{
                  padding: '10px 24px', border: '1px solid #d1d5db',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '500', minWidth: '100px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeDeleteFeedback}
                disabled={deleteLoading}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: '8px', background: deleteLoading ? '#9ca3af' : '#ef4444',
                  color: 'white', cursor: deleteLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '600', minWidth: '100px'
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Offers and Coupons Tab Component (with nested tabs) - Simplified, handlers in parent
const OffersAndCouponsTab = React.memo(({ offers, coupons, businessId, onRefresh, setShowOfferModal, setShowCouponModal, setEditingOffer, setEditingCoupon, showOfferModal, showCouponModal }) => {
  const [activeSubTab, setActiveSubTab] = useState('offers');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'offer'|'coupon', id, title }
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      let response;
      if (deleteConfirm.type === 'offer') {
        response = await AdminService.deleteOffer(businessId, deleteConfirm.id);
      } else {
        response = await AdminService.deleteCoupon(businessId, deleteConfirm.id);
      }
      if (response.success) {
        if (onRefresh) onRefresh();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting record');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  };

  // Offer handlers
  const handleAddOffer = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setEditingOffer(null);
    setShowOfferModal(true);
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setShowOfferModal(true);
  };

  const handleDeleteOffer = (offerId, offerTitle) => {
    setDeleteConfirm({ type: 'offer', id: offerId, title: offerTitle || 'this offer' });
  };

  // Coupon handlers
  const handleAddCoupon = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setEditingCoupon(null);
    setShowCouponModal(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setShowCouponModal(true);
  };

  const handleDeleteCoupon = (couponId, couponCode) => {
    setDeleteConfirm({ type: 'coupon', id: couponId, title: couponCode || 'this coupon' });
  };

  return (
    <div className="tab-offers-coupons">
      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div
          onClick={() => !deleteLoading && setDeleteConfirm(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 999999
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', padding: '32px',
              maxWidth: '420px', width: '90%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center'
            }}
          >
            {/* Icon */}
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#fee2e2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px'
            }}>
              <MdOutlineInfo style={{ color: '#dc2626', fontSize: '24px' }} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
              Delete {deleteConfirm.type === 'offer' ? 'Offer' : 'Coupon'}?
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong>"{deleteConfirm.title}"</strong>?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
                style={{
                  padding: '10px 24px', border: '1px solid #d1d5db',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '500', minWidth: '100px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: '8px', background: deleteLoading ? '#9ca3af' : '#ef4444',
                  color: 'white', cursor: deleteLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '600', minWidth: '100px'
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sub-tabs for Offers and Coupons */}
      <div className="sub-tabs">
        <button
          className={`sub-tab ${activeSubTab === 'offers' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('offers')}
        >
          Offers ({offers.length})
        </button>
        <button
          className={`sub-tab ${activeSubTab === 'coupons' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('coupons')}
        >
          Coupons ({coupons.length})
        </button>
      </div>

      {/* Sub-tab content */}
      <div className="sub-tab-content">
        {activeSubTab === 'offers' && (
          <OffersTab
            offers={offers}
            businessId={businessId}
            onRefresh={onRefresh}
            onAddOffer={handleAddOffer}
            onEditOffer={handleEditOffer}
            onDeleteOffer={handleDeleteOffer}
          />
        )}
        {activeSubTab === 'coupons' && (
          <CouponsTab
            coupons={coupons}
            businessId={businessId}
            onRefresh={onRefresh}
            onAddCoupon={handleAddCoupon}
            onEditCoupon={handleEditCoupon}
            onDeleteCoupon={handleDeleteCoupon}
          />
        )}
      </div>
    </div>
  );
});

// Offers Tab Component (for Promotional Offers)
const OffersTab = ({ offers, businessId, onRefresh, onAddOffer, onEditOffer, onDeleteOffer }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;
  const totalPages = Math.ceil(offers.length / PAGE_SIZE);
  const pagedOffers = offers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="tab-offers">
      <div className="offers-header">
        <h3>Promotional Offers ({offers.length})</h3>
        <button type="button" className="btn btn-primary" onClick={onAddOffer}>
          + Add Offer
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Discount</th>
              <th>Price</th>
              <th>Status</th>
              <th>Valid From</th>
              <th>Valid Until</th>
              <th>Views</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.length === 0 ? (
              <NoDataDisplay colSpan={9} message="No offers found" />
            ) : (
              pagedOffers.map(offer => (
                <tr key={offer.offer_id}>
                  <td><strong>{offer.title}</strong></td>
                  <td>{offer.offer_type}</td>
                  <td>
                    {offer.discount_percentage ? `${offer.discount_percentage}%` : ''}
                    {offer.discount_amount ? `?${offer.discount_amount}` : ''}
                  </td>
                  <td>
                    {offer.original_price && offer.offer_price ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#999' }}>₹{offer.original_price}</span>
                        {' '}?{offer.offer_price}
                      </>
                    ) : 'N/A'}
                  </td>
                  <td>
                    <span className={`badge ${offer.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {offer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{offer.valid_from ? new Date(offer.valid_from).toLocaleDateString() : 'N/A'}</td>
                  <td>{offer.valid_to ? new Date(offer.valid_to).toLocaleDateString() : 'N/A'}</td>
                  <td>{offer.current_views || 0} / {offer.max_views || '8'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => onEditOffer(offer)}
                        title="Edit Offer"
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => onDeleteOffer(offer.offer_id, offer.title)}
                        title="Delete Offer"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile-only cards - hidden on desktop */}
      <div className="offers-mobile-cards" style={{ display: 'none' }}>
        {offers.length === 0 ? (
          <NoDataDisplay message="No offers found" />
        ) : (
          offers.map(offer => (
            <div key={offer.offer_id} className="offer-card-mobile">
              <div className="offer-card-title">{offer.title}</div>
              <div className="offer-card-details">
                <div className="offer-detail-row">
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Type : </span>
                    <span className="offer-detail-value">{offer.offer_type}</span>
                  </div>
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Discount : </span>
                    <span className="offer-detail-value">
                      {offer.discount_percentage ? `${offer.discount_percentage}%` : ''}
                      {offer.discount_amount ? `?${offer.discount_amount}` : 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="offer-detail-row">
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Price : </span>
                    <span className="offer-detail-value">
                      {offer.original_price && offer.offer_price ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#999' }}>₹{offer.original_price}</span>
                          {' '}?{offer.offer_price}
                        </>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Status : </span>
                    <span className="offer-detail-value">
                      <span className={`badge ${offer.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="offer-detail-row">
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Valid From : </span>
                    <span className="offer-detail-value">{offer.valid_from ? new Date(offer.valid_from).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Valid Until : </span>
                    <span className="offer-detail-value">{offer.valid_to ? new Date(offer.valid_to).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
                <div className="offer-detail-row">
                  <div className="offer-detail-col">
                    <span className="offer-detail-label">Views : </span>
                    <span className="offer-detail-value">{offer.current_views || 0} / {offer.max_views || '8'}</span>
                  </div>
                </div>
              </div>
              <div className="offer-card-actions">
                <button
                  className="btn btn-icon btn-info"
                  onClick={() => onEditOffer(offer)}
                  title="Edit Offer"
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '8px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '36px',
                    height: '36px'
                  }}
                >
                  <FiEdit2 />
                </button>
                <button
                  className="btn btn-icon btn-danger"
                  onClick={() => onDeleteOffer(offer.offer_id, offer.title)}
                  title="Delete Offer"
                  style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '8px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '36px',
                    height: '36px'
                  }}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      <PaginationBar currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
};

// Coupons Tab Component
const CouponsTab = ({ coupons, businessId, onRefresh, onAddCoupon, onEditCoupon, onDeleteCoupon }) => {
  return (
    <div className="tab-offers">
      <div className="offers-header">
        <h3>Coupons ({coupons.length})</h3>
        <button type="button" className="btn btn-primary" onClick={onAddCoupon}>
          + Add Coupon
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Coupon Code</th>
              <th>Type</th>
              <th>Value</th>
              <th>Status</th>
              <th>Valid From</th>
              <th>Valid Until</th>
              <th>Usage</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <NoDataDisplay colSpan={8} message="No coupons found" />
            ) : (
              coupons.map(coupon => (
                <tr key={coupon.coupon_id}>
                  <td><strong>{coupon.code}</strong></td>
                  <td>{coupon.type}</td>
                  <td>{coupon.type === 'percentage' ? `${coupon.value}%` : `?${coupon.value}`}</td>
                  <td>
                    <span className={`badge ${coupon.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{coupon.valid_from ? new Date(coupon.valid_from).toLocaleDateString() : 'N/A'}</td>
                  <td>{coupon.valid_to ? new Date(coupon.valid_to).toLocaleDateString() : 'N/A'}</td>
                  <td>{coupon.current_usage || 0} / {coupon.max_usage || '8'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => onEditCoupon(coupon)}
                        title="Edit Coupon"
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => onDeleteCoupon(coupon.coupon_id, coupon.code)}
                        title="Delete Coupon"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile-only cards - hidden on desktop */}
      <div className="coupons-mobile-cards" style={{ display: 'none' }}>
        {coupons.length === 0 ? (
          <NoDataDisplay message="No coupons found" />
        ) : (
          coupons.map(coupon => (
            <div key={coupon.coupon_id} className="coupon-card-mobile">
              <div className="offer-card-title">{coupon.code}</div>
              <div className="offer-card-details">
                <div className="offer-detail-row">
                  <span className="offer-detail-label">Type</span>
                  <span className="offer-detail-value">{coupon.type}</span>
                </div>
                <div className="offer-detail-row">
                  <span className="offer-detail-label">Value</span>
                  <span className="offer-detail-value">{coupon.type === 'percentage' ? `${coupon.value}%` : `?${coupon.value}`}</span>
                </div>
                <div className="offer-detail-row">
                  <span className="offer-detail-label">Status</span>
                  <span className="offer-detail-value">
                    <span className={`badge ${coupon.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </span>
                </div>
                <div className="offer-detail-row">
                  <span className="offer-detail-label">Valid From</span>
                  <span className="offer-detail-value">{coupon.valid_from ? new Date(coupon.valid_from).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="offer-detail-row">
                  <span className="offer-detail-label">Valid Until</span>
                  <span className="offer-detail-value">{coupon.valid_to ? new Date(coupon.valid_to).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="offer-detail-row">
                  <span className="offer-detail-label">Usage</span>
                  <span className="offer-detail-value">{coupon.current_usage || 0} / {coupon.max_usage || '8'}</span>
                </div>
              </div>
              <div className="offer-card-actions">
                <button
                  className="btn btn-info"
                  onClick={() => onEditCoupon(coupon)}
                  title="Edit Coupon"
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => onDeleteCoupon(coupon.coupon_id, coupon.code)}
                  title="Delete Coupon"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};



// Operating Hours Tab Component
const OperatingHoursTab = ({ business }) => {
  let hours = {};
  try {
    if (business.business_hours) {
      hours = typeof business.business_hours === 'string'
        ? JSON.parse(business.business_hours)
        : business.business_hours;
    }
  } catch (e) {
    console.error('Error parsing business hours:', e);
    hours = {};
  }

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="tab-hours">
      <h3>Operating Hours</h3>
      <div className="hours-table">
        {days.map(day => (
          <div key={day} className="hours-row">
            <span className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
            <span className="hours-time">
              {hours[day] ? `${hours[day].open} - ${hours[day].close}` : 'Closed'}
            </span>
          </div>
        ))}
      </div>
      <button className="btn btn-primary">Edit Hours</button>
    </div>
  );
};

// Delivery Settings Tab Component
const DeliverySettingsTab = ({ business }) => (
  <div className="tab-delivery">
    <h3>Delivery Settings</h3>
    <div className="settings-form">
      <div className="form-group">
        <label>Delivery Radius (km)</label>
        <input type="number" defaultValue="5" className="form-input" />
      </div>
      <div className="form-group">
        <label>Delivery Fee (?)</label>
        <input type="number" defaultValue="30" className="form-input" />
      </div>
      <div className="form-group">
        <label>Free Delivery Above (?)</label>
        <input type="number" defaultValue="500" className="form-input" />
      </div>
      <button className="btn btn-primary">Save Settings</button>
    </div>
  </div>
);


// Security Tab Component
const SecurityTab = ({ business }) => (
  <div className="tab-security">
    <h3>Login & Security</h3>
    <div className="security-info">
      <p>Owner Email: {business.owner_email}</p>
      <p>Owner Mobile: {business.owner_mobile}</p>
      <button className="btn btn-warning">Reset Password</button>
      <button className="btn btn-danger">Block Login</button>
    </div>
  </div>
);

// Notifications Tab Component
const NotificationsTab = ({ businessId }) => (
  <div className="tab-notifications">
    <h3>Send Notification</h3>
    <div className="notification-form">
      <textarea
        className="form-textarea"
        placeholder="Enter notification message..."
        rows="5"
      ></textarea>
      <button className="btn btn-primary">Send Notification</button>
    </div>
  </div>
);

// Activity Logs Tab Component
const ActivityLogsTab = ({ businessId }) => (
  <div className="tab-logs">
    <h3>Activity Logs</h3>
    <p className="info-message">Activity logging system coming soon...</p>
  </div>
);

// Order Edit Modal Component
const OrderEditModal = ({ order, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    order_id: order?.order_id || '',
    order_type: order?.order_type || 'restaurant',
    status: order?.status || '',
    total_amount: order?.subtotal || order?.amount || 0,
    delivery_charges: order?.delivery_charges || 0,
    discount_amount: order?.discount || 0,
    final_amount: order?.amount || 0,
    delivery_instructions: order?.delivery_instructions || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Status options based on order type
  const statusOptions = formData.order_type === 'grocery'
    ? ['pending', 'confirmed', 'preparing', 'ready_for_pickup', 'picked_up', 'delivered', 'cancelled']
    : ['pending', 'confirmed', 'preparing', 'ready', 'picked_up', 'delivered', 'cancelled'];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      overflow: 'auto'
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '12px',
        minWidth: '500px',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#111827' }}>
            Edit Order #{order?.order_number}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            �
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Order Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Select Status</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>
                  {status.replace(/_/g, ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Total Amount (?)
            </label>
            <input
              type="number"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              step="0.01"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Delivery Charges (?)
            </label>
            <input
              type="number"
              name="delivery_charges"
              value={formData.delivery_charges}
              onChange={handleChange}
              step="0.01"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Discount Amount (?)
            </label>
            <input
              type="number"
              name="discount_amount"
              value={formData.discount_amount}
              onChange={handleChange}
              step="0.01"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Final Amount (?)
            </label>
            <input
              type="number"
              name="final_amount"
              value={formData.final_amount}
              onChange={handleChange}
              step="0.01"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#374151' }}>
              Delivery Instructions
            </label>
            <textarea
              name="delivery_instructions"
              value={formData.delivery_instructions}
              onChange={handleChange}
              rows="3"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                background: '#3b82f6',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Delivery Tab Component - Professional with React Icons
const DeliveryTab = ({ business }) => {
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch delivery partners
  const fetchDeliveryPartners = async () => {
    setLoading(true);
    try {
      const response = await AdminService.getDeliveryPartners(business.business_id);
      if (response.success) {
        setDeliveryPartners(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching delivery partners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (business.business_id) {
      fetchDeliveryPartners();
    }
  }, [business.business_id]);

  const handleManagePartners = () => {
    setShowPartnerModal(true);
  };

  const handleUpdateSettings = () => {
    setShowSettingsModal(true);
  };

  const handleViewAnalytics = () => {
    alert('Delivery Analytics feature coming soon!');
  };

  return (
    <div className="tab-delivery-new">
      {/* Top Row - Essential Stats Only (3 cards) */}
      <div className="delivery-stats-row-compact">
        <div className="delivery-stat-card">
          <div className="stat-icon-wrapper">
            <MdOutlineDeliveryDining className="stat-icon-react" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{business.active_delivery_partners || 0}</div>
            <div className="stat-label">Active Partners</div>
          </div>
        </div>

        <div className="delivery-stat-card">
          <div className="stat-icon-wrapper success">
            <MdOutlineCheckCircleOutline className="stat-icon-react" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{business.total_deliveries || 0}</div>
            <div className="stat-label">Total Deliveries</div>
          </div>
        </div>

        <div className="delivery-stat-card">
          <div className="stat-icon-wrapper warning">
            <MdOutlineSpeed className="stat-icon-react" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{business.avg_delivery_time || 'N/A'}</div>
            <div className="stat-label">Avg Time (min)</div>
          </div>
        </div>
      </div>

      {/* Middle Row - Settings & Performance (2 columns) */}
      <div className="delivery-middle-row">
        {/* Delivery Settings Card */}
        <div className="delivery-info-card">
          <div className="card-header">
            <h4>Delivery Settings</h4>
            <button className="btn-edit" onClick={handleUpdateSettings}>
              <MdOutlineEdit /> Edit
            </button>
          </div>
          <div className="settings-grid">
            <div className="setting-item-new">
              <div className="setting-icon-wrapper">
                <MdOutlineLocationOn className="setting-icon-react" />
              </div>
              <div className="setting-content">
                <span className="setting-label">Delivery Radius</span>
                <span className="setting-value">{business.delivery_radius || 'N/A'} km</span>
              </div>
            </div>
            <div className="setting-item-new">
              <div className="setting-icon-wrapper">
                <MdOutlineAttachMoney className="setting-icon-react" />
              </div>
              <div className="setting-content">
                <span className="setting-label">Delivery Fee</span>
                <span className="setting-value">₹{business.delivery_fee || '0'}</span>
              </div>
            </div>
            <div className="setting-item-new">
              <div className="setting-icon-wrapper">
                <MdOutlineLocalOffer className="setting-icon-react" />
              </div>
              <div className="setting-content">
                <span className="setting-label">Free Delivery Above</span>
                <span className="setting-value">₹{business.free_delivery_above || 'N/A'}</span>
              </div>
            </div>
            <div className="setting-item-new">
              <div className="setting-icon-wrapper">
                <MdOutlineAccessTime className="setting-icon-react" />
              </div>
              <div className="setting-content">
                <span className="setting-label">Estimated Time</span>
                <span className="setting-value">{business.estimated_delivery_time || '30-45'} min</span>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics Card */}
        <div className="delivery-info-card">
          <div className="card-header">
            <h4>Performance Metrics</h4>
            <button className="btn-view" onClick={handleViewAnalytics}>
              <MdOutlineBarChart /> Analytics
            </button>
          </div>
          <div className="performance-grid">
            <div className="performance-item">
              <div className="performance-label">Success Rate</div>
              <div className="performance-bar-container">
                <div className="performance-bar" style={{ width: `${business.delivery_success_rate || 0}%` }}>
                  <span className="performance-value">{business.delivery_success_rate || 0}%</span>
                </div>
              </div>
            </div>
            <div className="performance-item">
              <div className="performance-label">On-Time Delivery</div>
              <div className="performance-bar-container">
                <div className="performance-bar" style={{ width: `${business.on_time_delivery || 85}%`, backgroundColor: '#10b981' }}>
                  <span className="performance-value">{business.on_time_delivery || 85}%</span>
                </div>
              </div>
            </div>
            <div className="performance-item">
              <div className="performance-label">Customer Satisfaction</div>
              <div className="performance-bar-container">
                <div className="performance-bar" style={{ width: `${business.customer_satisfaction || 90}%`, backgroundColor: '#f59e0b' }}>
                  <span className="performance-value">{business.customer_satisfaction || 90}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row - Delivery Partners List */}
      <div className="delivery-partners-section">
        <div className="section-header">
          <h4>Delivery Partners</h4>
          <button className="btn btn-primary" onClick={handleManagePartners}>
            <MdOutlineAdd /> Add Partner
          </button>
        </div>

        {loading ? (
          <div className="loading-state">Loading partners...</div>
        ) : deliveryPartners.length > 0 ? (
          <div className="partners-grid">
            {deliveryPartners.map((partner, index) => (
              <div key={index} className="partner-card">
                <div className="partner-avatar-icon">
                  <MdOutlinePerson />
                </div>
                <div className="partner-info">
                  <div className="partner-name">{partner.name || `Partner ${index + 1}`}</div>
                  <div className="partner-phone">
                    <MdOutlinePhone className="phone-icon" />
                    {partner.phone || 'N/A'}
                  </div>
                </div>
                <div className="partner-status">
                  <span className={`status-badge ${partner.status || 'available'}`}>
                    {partner.status || 'Available'}
                  </span>
                </div>
                <div className="partner-stats-mini">
                  <span><MdOutlineDeliveryDining /> {partner.total_deliveries || 0}</span>
                  <span><MdOutlineStarOutline /> {partner.rating || 'N/A'}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon-wrapper">
              <MdOutlineDeliveryDining className="empty-icon-react" />
            </div>
            <p>No delivery partners found</p>
            <button className="btn btn-primary" onClick={handleManagePartners}>
              <MdOutlineAdd /> Add Your First Partner
            </button>
          </div>
        )}
      </div>

      {/* Partner Management Modal */}
      {showPartnerModal && (
        <div className="modal-overlay" onClick={() => setShowPartnerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Delivery Partners</h3>
              <button className="close-btn" onClick={() => setShowPartnerModal(false)}>�</button>
            </div>
            <div className="modal-body">
              <p>Partner management interface coming soon...</p>
              <p>You'll be able to:</p>
              <ul>
                <li>Add new delivery partners</li>
                <li>Edit partner details</li>
                <li>Assign/unassign partners</li>
                <li>View partner performance</li>
              </ul>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPartnerModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Delivery Settings</h3>
              <button className="close-btn" onClick={() => setShowSettingsModal(false)}>�</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Delivery Radius (km)</label>
                <input type="number" defaultValue={business.delivery_radius || 5} />
              </div>
              <div className="form-group">
                <label>Delivery Fee (?)</label>
                <input type="number" defaultValue={business.delivery_fee || 0} />
              </div>
              <div className="form-group">
                <label>Free Delivery Above (?)</label>
                <input type="number" defaultValue={business.free_delivery_above || 0} />
              </div>
              <div className="form-group">
                <label>Estimated Delivery Time (minutes)</label>
                <input type="text" defaultValue={business.estimated_delivery_time || '30-45'} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowSettingsModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => {
                alert('Settings saved successfully!');
                setShowSettingsModal(false);
              }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDetailsTabbed;
